import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { SkillsTreeNode, SkillsTreeOperator } from '../types';
import { DrawerTabKey, DrawerTabs } from './DrawerTabs';
import GlassPicklist, { type GlassPicklistOption } from '../../../components/ui/GlassPicklist';
import PortalAvatar from '../../../components/profile/PortalAvatar';
import { apiFetch } from '../../../lib/api';
import {
  normalizeTeamCapacitySummaryPayload,
  type TeamCapacitySummary,
} from '../../../lib/projects/teamCapacitySummary';
import {
  addOrganizationTreeRosterMember,
  applyProjectManagerApproval,
  removeOrganizationTreeRosterMember,
  createProjectManagerRun,
  createAIAgentAssignment,
  deleteAIAgentAssignment,
  ensurePlanningAgentFamilyDefinitions,
  fetchProjectManagerNodeClients,
  fetchBrainKBaseProcessOptions,
  listAIAgentAssignments,
  listProjectManagerRuns,
  reviewProjectManagerApproval,
  updateAIAgentAssignment,
  type BrainAIAgentAssignment,
  type BrainAIAgentDefinition,
  type BrainKBaseProcessOption,
  type BrainProjectManagerRun,
  type ServiceRoutingOption,
} from '../api';

type NodeMetaDraft = {
  position: string;
  team: string;
  designation: string;
  childDesignation: string;
  appointedAgent: string;
  appointedAgentUid: string;
  appointedAgentEmail: string;
  appointedAgentAvatarUrl: string;
};

type NodeDetailsDrawerProps = {
  node: SkillsTreeNode | null;
  allNodes: SkillsTreeNode[];
  open: boolean;
  operatorOptions: SkillsTreeOperator[];
  serviceRoutingOptions: ServiceRoutingOption[];
  serviceRoutingOptionsLoading: boolean;
  operatorsLoading: boolean;
  canEdit: boolean;
  onClose: () => void;
  onTitleChange: (id: string, title: string) => void;
  onMetaChange: (id: string, meta: NodeMetaDraft) => void;
  onRulesChange: (id: string, rules: Record<string, unknown>) => void;
};

type TeamSettingsDraft = {
  roundRobinEnabled: boolean;
  teamCapacityHours: number;
};

type TeamMemberCapacitySnapshot = {
  operator: SkillsTreeOperator;
  assignedTicketCount: number;
  remainingEstimateMinutes: number;
  loadHours: number;
  capacityRatio: number;
  remainingCapacityHours: number;
  membershipRole: string | null;
  isPrimaryMembership: boolean;
  isRosterMember: boolean;
  isOutsideRoster: boolean;
};

const CHILD_SCOPE_DRAWER_WIDTH = 'clamp(300px, 28vw, 360px)';

function GenerativeAiSparkIcon({
  size = 16,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5-4.4-1.6 4.4-1.6L12 3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="currentColor"
        fillOpacity="0.16"
      />
      <path d="M19 4l.5 1.5L21 6l-1.5.5L19 8l-.5-1.5L17 6l1.5-.5L19 4z" fill="currentColor" />
      <path d="M5 15l.5 1.5L7 17l-1.5.5L5 19l-.5-1.5L3 17l1.5-.5L5 15z" fill="currentColor" />
    </svg>
  );
}

function TrashCanIcon({
  size = 14,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4 7h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M9 3h6l1 2h4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 7l1 12a2 2 0 0 0 2 1.8h4a2 2 0 0 0 2-1.8L17 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const DrawerBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 140;
`;

const Drawer = styled.aside<{ $open: boolean; $childOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: ${({ $childOpen }) => ($childOpen ? CHILD_SCOPE_DRAWER_WIDTH : '0')};
  width: clamp(420px, 72vw, 780px);
  height: 100dvh;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #E8EAED;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.45);
  transform: translateX(${({ $open }) => ($open ? '0%' : '104%')});
  transition: transform 180ms ease, right 180ms ease;
  z-index: 150;
  display: grid;
  grid-template-rows: auto auto 1fr;
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  overflow: hidden;
  color-scheme: dark;

  @media (max-width: 860px) {
    width: min(100vw, 100%);
    right: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const DrawerHeader = styled.div`
  position: relative;
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 6px;
`;

const DrawerTitleWrap = styled.div`
  padding-right: 48px;
  min-width: 0;
  display: grid;
  gap: 4px;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  color: #E8EAED;
  font-size: 1.1rem;
  line-height: 1.1;
  font-weight: 900;
`;

const DrawerSub = styled.p`
  margin: 0;
  color: #9AA4B2;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 700;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #CBD2E1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const TabsWrap = styled.div`
  padding: 10px 12px 0;
`;

const DrawerBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 14px 12px 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 999px;
  }
`;

const Panel = styled.section`
  display: grid;
  gap: 12px;
  padding-top: 2px;

  & + & {
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const Field = styled.label`
  position: relative;
  display: grid;
  gap: 6px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.18px;
  color: #9AA4B2;
  font-weight: 900;
`;

const FlatInteractiveFieldStyles = css`
  background: transparent;
  border: 1px solid transparent;
  color: rgba(245, 248, 252, 0.96);
  border-radius: 8px;
  font-weight: 700;
  outline: none;
  box-shadow: none;

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    background: rgba(255, 255, 255, 0.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
    outline: 2px solid rgba(138, 180, 248, 0.35);
    outline-offset: 1px;
  }
`;

const Input = styled.input`
  min-height: 38px;
  width: 100%;
  padding: 8px 10px;
  ${FlatInteractiveFieldStyles}
  font-size: 13px;
  font-weight: 700;
`;

const Select = styled.select`
  min-height: 38px;
  width: 100%;
  padding: 8px 10px;
  ${FlatInteractiveFieldStyles}
  font-size: 13px;
  font-weight: 700;
  appearance: none;
  cursor: pointer;
  background: rgb(29, 33, 42);
  color: rgba(245, 248, 252, 0.96);
  color-scheme: dark;

  option {
    background: rgb(29, 33, 42);
    color: rgba(245, 248, 252, 0.96);
  }
`;

const Textarea = styled.textarea`
  min-height: 88px;
  width: 100%;
  padding: 10px;
  resize: vertical;
  ${FlatInteractiveFieldStyles}
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
`;

const Placeholder = styled.section`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  line-height: 1.45;
  font-weight: 700;
`;

const AIPanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const AIPanelTitleWrap = styled.div`
  display: grid;
  gap: 3px;
`;

const AIPanelTitle = styled.h4`
  margin: 0;
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.9rem;
  font-weight: 900;
`;

const AIPanelSub = styled.p`
  margin: 0;
  color: rgba(196, 208, 232, 0.82);
  font-size: 0.72rem;
  line-height: 1.35;
  font-weight: 700;
`;

const AIAgentAccordionCard = styled.section<{ $expanded?: boolean }>`
  display: grid;
  gap: ${({ $expanded }) => ($expanded ? '12px' : '0')};
  border-radius: 16px;
  border: 1px solid
    ${({ $expanded }) =>
      $expanded ? 'rgba(224, 161, 94, 0.24)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${({ $expanded }) =>
    $expanded ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.025)'};
  overflow: hidden;
`;

const AIAgentAccordionHeader = styled.div<{ $expanded?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
`;

const AIAgentAccordionToggle = styled.button<{ $expanded?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 0;
  padding: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const AIAgentIconButton = styled.button<{ $tone?: 'default' | 'danger' }>`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'danger'
        ? 'rgba(255, 125, 125, 0.22)'
        : 'rgba(255, 255, 255, 0.08)'};
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? 'rgba(255, 94, 94, 0.08)'
      : 'rgba(255, 255, 255, 0.04)'};
  color: ${({ $tone }) =>
    $tone === 'danger'
      ? 'rgba(255, 182, 182, 0.95)'
      : 'rgba(236, 242, 255, 0.9)'};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ $tone }) =>
      $tone === 'danger'
        ? 'rgba(255, 94, 94, 0.14)'
        : 'rgba(255, 255, 255, 0.08)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AIAgentAccordionInfo = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const AIAgentAccordionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AIAgentAccordionExpand = styled.span<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(236, 242, 255, 0.9);
  font-size: 0.82rem;
  font-weight: 900;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 160ms ease, background 160ms ease, border-color 160ms ease;
`;

const AIAgentAccordionBody = styled.div`
  display: grid;
  gap: 14px;
  padding: 0 16px 16px;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 190;
  background: rgba(0, 0, 0, 0.55);
  display: grid;
  place-items: center;
  padding: 16px;
`;

const ConfirmDialog = styled.div`
  width: min(100%, 420px);
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(16, 18, 24, 0.98));
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
`;

const ConfirmTitle = styled.h4`
  margin: 0;
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.95rem;
  font-weight: 900;
`;

const ConfirmBody = styled.p`
  margin: 0;
  color: rgba(207, 216, 232, 0.9);
  font-size: 0.82rem;
  line-height: 1.5;
  font-weight: 700;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const AIAgentSubtabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const AIAgentSubtab = styled.button<{ $active?: boolean }>`
  min-height: 34px;
  margin-bottom: -1px;
  border-radius: 10px 10px 0 0;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.1)')};
  border-bottom-color: transparent;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)')};
  color: ${({ $active }) => ($active ? 'rgba(248, 235, 214, 0.98)' : 'rgba(229, 235, 245, 0.9)')};
  padding: 0 12px;
  font-size: 0.72rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.06)')};
    border-color: rgba(224, 161, 94, 0.22);
    border-bottom-color: transparent;
  }
`;

const AIAgentSection = styled.div`
  display: grid;
  gap: 14px;
`;

const AIAgentRunCard = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const AIAgentRunHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const AIAgentRunTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.82rem;
  font-weight: 900;
`;

const AIAgentRunMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 700;
`;

const AIAgentRunActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AIAgentInlineButton = styled.button<{ $tone?: 'default' | 'danger' }>`
  min-height: 30px;
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'danger' ? 'rgba(255, 128, 128, 0.32)' : 'rgba(255, 255, 255, 0.16)'};
  background: ${({ $tone }) =>
    $tone === 'danger' ? 'rgba(140, 46, 46, 0.16)' : 'transparent'};
  color: rgba(245, 248, 252, 0.96);
  padding: 0 12px;
  font-size: 0.72rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AIAgentList = styled.div`
  display: grid;
  gap: 12px;
`;

const ClientAccordionCard = styled.div<{ $expanded?: boolean }>`
  border-radius: 14px;
  border: 1px solid
    ${({ $expanded }) => ($expanded ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
`;

const ClientAccordionHeader = styled.button<{ $expanded?: boolean }>`
  width: 100%;
  border: none;
  background: ${({ $expanded }) => ($expanded ? 'rgba(224, 161, 94, 0.08)' : 'transparent')};
  color: inherit;
  padding: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  text-align: left;
  cursor: pointer;
`;

const ClientAccordionInfo = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const ClientAccordionTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.84rem;
  font-weight: 900;
  line-height: 1.2;
`;

const ClientAccordionMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 700;
`;

const ClientAccordionExpand = styled.span<{ $expanded?: boolean }>`
  color: rgba(203, 210, 225, 0.88);
  font-size: 0.9rem;
  font-weight: 900;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 140ms ease;
`;

const ClientAccordionBody = styled.div`
  display: grid;
  gap: 14px;
  padding: 0 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const ClientInlineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ClientInlineMeta = styled.div`
  display: grid;
  gap: 3px;
`;

const ClientScopeButton = styled.button`
  min-height: 32px;
  border-radius: 999px;
  border: 1px dashed rgba(224, 161, 94, 0.34);
  background: transparent;
  color: rgba(245, 248, 252, 0.94);
  padding: 0 12px;
  font-size: 0.72rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AIChatLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const AIChatIcon = styled(GenerativeAiSparkIcon)`
  color: rgba(224, 161, 94, 0.92);
  flex: 0 0 auto;
`;

const ChildDrawerBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: none;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.18);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 145;
`;

const ChildDrawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: ${CHILD_SCOPE_DRAWER_WIDTH};
  height: 100dvh;
  background: linear-gradient(180deg, rgba(18, 20, 25, 0.98), rgba(16, 18, 24, 0.98));
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: -12px 0 32px rgba(0, 0, 0, 0.34);
  transform: translateX(${({ $open }) => ($open ? '0%' : '104%')});
  transition: transform 180ms ease;
  z-index: 160;
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  overflow: hidden;

  @media (max-width: 860px) {
    width: min(100vw, 100%);
  }
`;

const ChildDrawerHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ChildDrawerTitleWrap = styled.div`
  display: grid;
  gap: 4px;
`;

const ChildDrawerTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.84rem;
  font-weight: 900;
`;

const ChildDrawerSub = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 700;
`;

const ChildDrawerClose = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(245, 248, 252, 0.88);
  font-size: 0.95rem;
  font-weight: 900;
  cursor: pointer;
`;

const ChildDrawerQuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 14px 0;
`;

const ScopeList = styled.div`
  display: grid;
  gap: 18px;
  padding: 12px 14px 14px;
  min-height: 0;
  overflow-y: auto;
`;

const ScopeSection = styled.section`
  display: grid;
  gap: 4px;
`;

const ScopeSectionHeader = styled.div`
  display: grid;
  gap: 2px;
  padding-bottom: 4px;
`;

const ScopeSectionTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ScopeSectionHint = styled.div`
  color: rgba(186, 198, 224, 0.72);
  font-size: 0.68rem;
  line-height: 1.35;
  font-weight: 700;
`;

const ScopeRows = styled.div`
  display: grid;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ScopeRowButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: 0;
  border-bottom: 1px solid
    ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.26)' : 'rgba(255, 255, 255, 0.08)')};
  background: transparent;
  color: inherit;
  padding: 12px 2px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  text-align: left;
  cursor: pointer;
  transition:
    background 160ms ease,
    border-color 160ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const ScopeCheck = styled.span<{ $active?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.66)' : 'rgba(255, 255, 255, 0.28)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.2)' : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  &::after {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${({ $active }) => ($active ? 'rgba(248, 235, 214, 0.98)' : 'transparent')};
  }
`;

const ScopeRowCopy = styled.div`
  display: grid;
  gap: 4px;
`;

const ScopeRowTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.76rem;
  font-weight: 900;
`;

const ScopeRowHint = styled.div`
  color: rgba(186, 198, 224, 0.8);
  font-size: 0.7rem;
  line-height: 1.35;
  font-weight: 700;
`;

const ChildDrawerFooter = styled.div`
  padding: 12px 14px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const AIAgentKeyValueGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const AIAgentKeyValue = styled.div`
  display: grid;
  gap: 4px;
`;

const AIAgentKey = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const AIAgentValue = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.4;
`;

const AIAgentBulletList = styled.div`
  display: grid;
  gap: 6px;
`;

const AIAgentBullet = styled.div`
  color: rgba(229, 235, 245, 0.9);
  font-size: 0.76rem;
  line-height: 1.45;
  font-weight: 700;
`;

const AIPanelNotice = styled.div<{ $tone?: 'default' | 'error' }>`
  border-radius: 8px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'error' ? 'rgba(255, 128, 128, 0.34)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${({ $tone }) =>
    $tone === 'error' ? 'rgba(140, 46, 46, 0.18)' : 'rgba(255, 255, 255, 0.03)'};
  color: ${({ $tone }) =>
    $tone === 'error' ? 'rgba(255, 214, 214, 0.96)' : 'rgba(229, 235, 245, 0.84)'};
  padding: 9px 10px;
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 700;
`;

const AIStatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusChip = styled.span<{ $tone?: 'default' | 'success' | 'warning' }>`
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'success'
        ? 'rgba(120, 219, 150, 0.42)'
        : $tone === 'warning'
          ? 'rgba(224, 161, 94, 0.42)'
          : 'rgba(255, 255, 255, 0.18)'};
  background: ${({ $tone }) =>
    $tone === 'success'
      ? 'rgba(68, 132, 87, 0.18)'
      : $tone === 'warning'
        ? 'rgba(224, 161, 94, 0.14)'
        : 'rgba(255, 255, 255, 0.04)'};
  color: rgba(245, 248, 252, 0.94);
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  font-size: 0.72rem;
  font-weight: 900;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  min-height: 34px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(120, 219, 150, 0.34)' : 'rgba(255, 255, 255, 0.18)')};
  background: ${({ $active }) => ($active ? 'rgba(68, 132, 87, 0.14)' : 'transparent')};
  color: rgba(245, 248, 252, 0.94);
  padding: 0 12px;
  font-size: 0.72rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AIReadonlyBlock = styled.div`
  display: grid;
  gap: 8px;
`;

const AIReadonlyRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const AIReadonlyLabel = styled.span`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AIReadonlyValue = styled.span`
  color: rgba(245, 248, 252, 0.94);
  font-size: 0.82rem;
  font-weight: 800;
`;

const AISaveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const AISaveHint = styled.span`
  color: rgba(186, 198, 224, 0.8);
  font-size: 0.72rem;
  font-weight: 700;
`;

const AISaveButton = styled.button`
  min-height: 36px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(224, 161, 94, 0.14);
  color: rgba(245, 248, 252, 0.96);
  padding: 0 14px;
  font-size: 0.74rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TeamSettingsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  padding: 12px;
  display: grid;
  gap: 8px;
`;

const TeamFlatSection = styled.div`
  display: grid;
  gap: 8px;
`;

const TeamSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const TeamInlineAction = styled.button`
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: rgba(245, 248, 252, 0.94);
  padding: 0 10px;
  font-size: 0.7rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const TeamSettingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
`;

const TeamSettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ModeStatusWrap = styled.div`
  display: grid;
  gap: 2px;
  justify-items: end;
  text-align: right;
`;

const ModeStatusTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.78rem;
  font-weight: 900;
  line-height: 1.1;
`;

const ModeStatusMeta = styled.div`
  color: rgba(186, 198, 224, 0.78);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.3;
`;

const TeamSettingTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.8rem;
  font-weight: 900;
`;

const TeamSettingMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.35;
  font-weight: 700;
`;

const SwitchButton = styled.button<{ $active?: boolean }>`
  width: 54px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(110,214,171,.42)' : 'rgba(255,255,255,.18)')};
  background: ${({ $active }) => ($active ? 'rgba(68,132,87,.22)' : 'rgba(255,255,255,.06)')};
  position: relative;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SwitchThumb = styled.span<{ $active?: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $active }) => ($active ? '27px' : '3px')};
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#6ed6ab' : 'rgba(255,255,255,.82)')};
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
  transition: left 120ms ease, background 120ms ease;
`;

const CompactMetricStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CompactMetricChip = styled.div`
  min-width: 120px;
  display: grid;
  gap: 2px;
  align-content: start;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px dashed rgba(224, 161, 94, 0.34);
  background: transparent;
`;

const CompactMetricValue = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.94rem;
  font-weight: 900;
  line-height: 1.1;
`;

const CompactMetricLabel = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TeamMembersList = styled.div`
  display: grid;
  gap: 10px;
`;

const TeamMemberRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 10px;
`;

const TeamMemberName = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.84rem;
  font-weight: 900;
`;

const TeamMemberMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.35;
  font-weight: 700;
`;

const TeamLoadWrap = styled.div`
  display: grid;
  gap: 6px;
  justify-items: end;
  min-width: 150px;
`;

const TeamMemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TeamMemberAction = styled.button`
  min-height: 24px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: transparent;
  color: rgba(245, 248, 252, 0.92);
  padding: 0 9px;
  font-size: 0.66rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const TeamLoadValue = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.82rem;
  font-weight: 900;
`;

const TeamLoadBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const TeamLoadFill = styled.div<{ $tone?: 'low' | 'watch' | 'high'; $width: number }>`
  width: ${({ $width }) => `${$width}%`};
  height: 100%;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'high'
      ? 'linear-gradient(90deg, rgba(248,113,113,.96), rgba(239,68,68,.96))'
      : $tone === 'watch'
        ? 'linear-gradient(90deg, rgba(245,158,11,.96), rgba(251,191,36,.96))'
        : 'linear-gradient(90deg, rgba(110,214,171,.96), rgba(72,187,120,.96))'};
`;

const PermissionGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const PermissionGroupHeader = styled.div`
  display: grid;
  gap: 3px;
`;

const PermissionGroupTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.76rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PermissionGroupHint = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.72rem;
  line-height: 1.35;
  font-weight: 700;
`;

const RunFlowSwitchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RunFlowSwitch = styled.button<{ $active?: boolean }>`
  min-height: 32px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.42)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.12)' : 'transparent'};
  color: ${({ $active }) =>
    $active ? 'rgba(248, 235, 214, 0.98)' : 'rgba(229, 235, 245, 0.92)'};
  padding: 0 12px;
  font-size: 0.74rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RunFlowHint = styled.div`
  color: rgba(186, 198, 224, 0.8);
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 700;
`;

const ProposalConversation = styled.div`
  display: grid;
  gap: 12px;
`;

const ProposalConversationTimeline = styled.div`
  display: grid;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ProposalConversationMessage = styled.div<{ $role: 'assistant' | 'user' | 'system' }>`
  display: grid;
  gap: 6px;
  justify-items: ${({ $role }) => ($role === 'user' ? 'end' : 'start')};
`;

const ProposalConversationMeta = styled.div<{ $role: 'assistant' | 'user' | 'system' }>`
  color: rgba(186, 198, 224, 0.78);
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1.35;
  text-align: ${({ $role }) => ($role === 'user' ? 'right' : 'left')};
`;

const ProposalConversationBubble = styled.div<{ $role: 'assistant' | 'user' | 'system' }>`
  width: min(100%, 560px);
  border-radius: 14px;
  border: 1px solid
    ${({ $role }) =>
      $role === 'user'
        ? 'rgba(138, 180, 248, 0.28)'
        : $role === 'system'
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(224, 161, 94, 0.28)'};
  background: ${({ $role }) =>
    $role === 'user'
      ? 'rgba(138, 180, 248, 0.08)'
      : $role === 'system'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(224, 161, 94, 0.08)'};
  padding: 10px 12px;
  display: grid;
  gap: 8px;
`;

const ProposalConversationTitle = styled.div`
  color: rgba(245, 248, 252, 0.96);
  font-size: 0.76rem;
  font-weight: 900;
`;

const ProposalConversationText = styled.div`
  color: rgba(229, 235, 245, 0.92);
  font-size: 0.74rem;
  line-height: 1.45;
  font-weight: 700;
  white-space: pre-wrap;
`;

const ProposalConversationList = styled.div`
  display: grid;
  gap: 6px;
`;

const ProposalConversationItem = styled.div`
  color: rgba(229, 235, 245, 0.9);
  font-size: 0.72rem;
  line-height: 1.45;
  font-weight: 700;
`;

const ProposalConversationActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProposalConversationComposer = styled.div`
  display: grid;
  gap: 8px;
`;

const ProposalConversationComposerRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessFinder = styled.div`
  position: relative;
  min-width: 0;
`;

const ProcessTrigger = styled.button<{ $open?: boolean }>`
  width: 100%;
  min-height: 38px;
  padding: 7px 36px 7px 10px;
  ${FlatInteractiveFieldStyles}
  text-align: left;
  cursor: pointer;
  position: relative;
  color: ${({ $open }) => ($open ? 'rgba(245, 248, 252, 0.96)' : 'rgba(203, 210, 225, 0.94)')};

  &::after {
    content: '▾';
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(203, 210, 225, 0.92);
    font-size: 12px;
    pointer-events: none;
  }
`;

const ProcessTriggerValue = styled.span<{ $placeholder?: boolean }>`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder }) => ($placeholder ? 'rgba(154, 164, 178, 0.96)' : 'rgba(232, 234, 237, 0.96)')};
`;

const ProcessPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 40;
  display: grid;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(18, 20, 25, 0.98);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.36);
`;

const ProcessSearchInput = styled.input`
  width: 100%;
  min-height: 38px;
  ${FlatInteractiveFieldStyles}
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 700;

  &::placeholder {
    color: rgba(154, 164, 178, 0.96);
  }
`;

const ProcessResults = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  padding-right: 2px;
`;

const ProcessResultSection = styled.div`
  display: grid;
  gap: 6px;
`;

const ProcessResultHeading = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.24px;
  text-transform: uppercase;
`;

const ProcessResultButton = styled.button<{ $active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: grid;
  gap: 4px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.42)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.12)' : 'rgba(255, 255, 255, 0.03)')};

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(224, 161, 94, 0.3);
  }
`;

const ProcessResultTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ProcessResultTitle = styled.div`
  color: rgba(232, 234, 237, 0.96);
  font-size: 13px;
  font-weight: 900;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProcessResultBadge = styled.span`
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(203, 210, 225, 0.96);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const ProcessResultMeta = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
`;

const ProcessEmptyState = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 2px;
`;

const AgentPickerShell = styled.div`
  position: relative;
`;

const AgentPickerInputRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
`;

const AgentPickerMenu = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(18, 20, 25, 0.98);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  max-height: 220px;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.26);
    border-radius: 999px;
  }
`;

const AgentOption = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  color: rgba(245, 247, 251, 0.95);
  display: grid;
  gap: 2px;
  text-align: left;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 800;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const AgentOptionSub = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(200, 210, 238, 0.8);
`;

const AgentPickerEmpty = styled.div`
  padding: 10px;
  font-size: 0.75rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.76);
`;

const RuleEditor = styled.div`
  display: grid;
  gap: 8px;
`;

const RulePillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RulePill = styled.span`
  min-height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(245, 248, 252, 0.94);
  background: rgba(255, 255, 255, 0.06);
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: none;
  letter-spacing: normal;
`;

const RulePillRemove = styled.button`
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.65rem;
  line-height: 1;
`;

const RuleInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const RuleAddButton = styled.button`
  min-height: 38px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
  color: rgba(242, 242, 242, 0.95);
  padding: 0 12px;
  font-size: 0.74rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RuleHint = styled.span`
  font-size: 11px;
  color: rgba(184, 198, 228, 0.78);
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
`;

const RoutingHint = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: rgba(213, 224, 248, 0.82);
  font-weight: 700;
`;

function toMetaText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normalizeDesignation(value: string): string {
  const cleaned = value.replace(/[^a-zA-Z0-9\s]/g, ' ');
  const normalized = cleaned.replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  return normalized
    .split(' ')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');
}

function singularizeDesignation(value: string): string {
  const normalized = normalizeDesignation(value);
  if (!normalized) return '';
  if (/ies$/i.test(normalized)) return normalized.replace(/ies$/i, 'y');
  if (/s$/i.test(normalized) && !/ss$/i.test(normalized)) return normalized.slice(0, -1);
  return normalized;
}

function parseChildDesignationRules(value: string): string[] {
  const unique = new Set<string>();
  value
    .split(/[\n,;|]+/g)
    .map((item) => singularizeDesignation(item.trim()))
    .filter(Boolean)
    .forEach((item) => {
      const key = item.toLowerCase();
      if (!key) return;
      if (![...unique].some((existing) => existing.toLowerCase() === key)) {
        unique.add(item);
      }
    });
  return [...unique];
}

function serializeChildDesignationRules(rules: string[]): string {
  return rules.map((item) => item.trim()).filter(Boolean).join(', ');
}

type NodeRoutingRule = {
  serviceCode: string;
  tierSkuId: string | null;
};

type InheritedNodeRoutingRule = NodeRoutingRule & {
  sourceNodeId: string;
  sourceNodeTitle: string;
};

type ProjectManagerAssignmentDraft = {
  agentCode: string;
  active: boolean;
  primaryProcessId: string;
  clientScopes: string[];
  autonomyMode: 'human_in_loop' | 'autonomous';
  clientOverrides: ProjectManagerClientOverride[];
};

type ProjectManagerViewTab = 'config' | 'logs';

type ProjectManagerNodeProjectOption = {
  id: string;
  name: string;
  orgId: string;
  orgName: string;
};

type ProjectManagerClientOverride = {
  orgId: string;
  autonomyMode: 'human_in_loop' | 'autonomous';
  scopes: string[] | null;
};

type ProjectManagerClientAccount = {
  id: string;
  name: string;
  ownerEmail: string | null;
  assignedServiceTeam: string | null;
  assignedServiceTeams: string[];
  primaryServiceActive: boolean | null;
  serviceLabels: string[];
  projectCountOnNode: number;
  latestProjectUpdatedAt: string | null;
  latestRunAt: string | null;
  latestSubscriptionUpdatedAt: string | null;
  sources: string[];
  source: 'organization' | 'project' | 'run';
};

type ProjectManagerClientScopeOption = {
  key: string;
  label: string;
  hint: string;
};

const LEGACY_PROJECT_MANAGER_AGENT_CODE = 'project-manager-agent';
const PLATFORM_PROJECT_MANAGER_AGENT_CODE = 'platform-project-manager-agent';
const DESIGN_PROJECT_MANAGER_AGENT_CODE = 'design-project-manager-agent';
const MARKETING_PROJECT_MANAGER_AGENT_CODE = 'marketing-project-manager-agent';
const PROGRAM_MANAGER_AGENT_CODE = 'program-manager-agent';
const SERVICE_PROJECT_MANAGER_AGENT_CODES = [
  LEGACY_PROJECT_MANAGER_AGENT_CODE,
  PLATFORM_PROJECT_MANAGER_AGENT_CODE,
  DESIGN_PROJECT_MANAGER_AGENT_CODE,
  MARKETING_PROJECT_MANAGER_AGENT_CODE,
];
const PROJECT_MANAGER_CLIENT_SCOPE_SECTIONS = [
  {
    key: 'projects',
    title: 'Projects',
    hint: 'Control whether the PM agent can create, update, and date project-level work.',
    scopes: [
      {
        key: 'project_create',
        label: 'Create projects',
        hint: 'Allow the PM agent to create new projects for this client.',
      },
      {
        key: 'project_update',
        label: 'Update existing projects',
        hint: 'Allow edits to project brief, definition of done, and other project context.',
      },
      {
        key: 'project_schedule',
        label: 'Schedule project dates',
        hint: 'Allow planned start and planned end dates to be set or revised on projects.',
      },
    ],
  },
  {
    key: 'milestones',
    title: 'Milestones',
    hint: 'Control whether the PM agent can build and maintain milestone structures.',
    scopes: [
      {
        key: 'milestone_create',
        label: 'Create milestones',
        hint: 'Allow the PM agent to create new milestones within a project.',
      },
      {
        key: 'milestone_update',
        label: 'Update existing milestones',
        hint: 'Allow edits to milestone context, sequencing, and definition of done.',
      },
      {
        key: 'milestone_schedule',
        label: 'Schedule milestone dates',
        hint: 'Allow planned start and planned end dates to be set or revised on milestones.',
      },
    ],
  },
  {
    key: 'tickets',
    title: 'Tickets',
    hint: 'Control what execution work the PM agent can create and date.',
    scopes: [
      {
        key: 'ticket_create',
        label: 'Create tickets',
        hint: 'Allow the PM agent to create tickets under projects and milestones.',
      },
      {
        key: 'ticket_schedule',
        label: 'Schedule ticket dates',
        hint: 'Allow planned start and due dates to be set on created tickets.',
      },
    ],
  },
  {
    key: 'automation',
    title: 'Automation',
    hint: 'Control how aggressively the PM agent can reshape or repeat existing work.',
    scopes: [
      {
        key: 'replanning',
        label: 'Replan existing work',
        hint: 'Allow revising existing projects and milestones instead of only creating net-new work.',
      },
      {
        key: 'recurring_cycles',
        label: 'Recurring cycles',
        hint: 'Allow recurring project duplication and next-cycle planning.',
      },
    ],
  },
] as const;
const PROJECT_MANAGER_CLIENT_SCOPE_OPTIONS: ProjectManagerClientScopeOption[] =
  PROJECT_MANAGER_CLIENT_SCOPE_SECTIONS.reduce<ProjectManagerClientScopeOption[]>(
    (all, section) => [...all, ...section.scopes],
    [],
);
const PROJECT_MANAGER_CLIENT_SCOPE_KEYS = PROJECT_MANAGER_CLIENT_SCOPE_OPTIONS.map(
  (scope) => scope.key,
);
const PROJECT_MANAGER_CLIENT_SCOPE_LEGACY_MAP: Record<string, string[]> = {
  projects: ['project_create'],
  milestones: ['milestone_create'],
  tickets: ['ticket_create'],
  scheduling: ['project_schedule', 'milestone_schedule', 'ticket_schedule'],
  replanning: ['project_update', 'milestone_update', 'replanning'],
  recurring_cycles: ['recurring_cycles'],
};
const DEFAULT_PROJECT_MANAGER_CLIENT_SCOPES = [...PROJECT_MANAGER_CLIENT_SCOPE_KEYS];
function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Unable to load the AI agent configuration.';
}

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const next: string[] = [];
  values.forEach((value) => {
    const normalized = value.trim();
    if (!normalized) return;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    next.push(normalized);
  });
  return next;
}

function normalizeProjectManagerAutonomyMode(
  value: unknown,
): 'human_in_loop' | 'autonomous' {
  return cleanText(value).toLowerCase() === 'human_in_loop'
    ? 'human_in_loop'
    : 'autonomous';
}

function readProjectManagerDefaultAutonomyMode(
  metadata: Record<string, unknown> | null | undefined,
): 'human_in_loop' | 'autonomous' {
  const explicitMode = cleanText(metadata?.autonomyMode);
  if (explicitMode) {
    return normalizeProjectManagerAutonomyMode(explicitMode);
  }
  return metadata?.requireHumanApproval === true
    ? 'human_in_loop'
    : 'autonomous';
}

function normalizeProjectManagerClientScopes(value: unknown): string[] {
  if (!Array.isArray(value)) return [...DEFAULT_PROJECT_MANAGER_CLIENT_SCOPES];
  const allowed = new Set<string>(PROJECT_MANAGER_CLIENT_SCOPE_KEYS);
  const expanded = toStringArray(value).flatMap(
    (entry) => PROJECT_MANAGER_CLIENT_SCOPE_LEGACY_MAP[entry] ?? [entry],
  );
  const next = expanded.filter((entry) => allowed.has(entry));
  return uniqueStrings(next);
}

function sameProjectManagerClientScopes(left: string[], right: string[]) {
  const normalize = (values: string[]) =>
    uniqueStrings(normalizeProjectManagerClientScopes(values)).sort();
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

function readProjectManagerClientOverrides(
  value: unknown,
): ProjectManagerClientOverride[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map((entry) => {
      const record = asObject(entry);
      const orgId = cleanText(record?.orgId);
      if (!orgId || seen.has(orgId)) return null;
      seen.add(orgId);
      return {
        orgId,
        autonomyMode: normalizeProjectManagerAutonomyMode(record?.autonomyMode),
        scopes: Array.isArray(record?.scopes)
          ? normalizeProjectManagerClientScopes(record?.scopes)
          : null,
      };
    })
    .filter(
      (entry): entry is ProjectManagerClientOverride => Boolean(entry),
    )
    .sort((left, right) => left.orgId.localeCompare(right.orgId));
}

function readProjectManagerAssignmentDraft(
  assignment: BrainAIAgentAssignment | null,
): ProjectManagerAssignmentDraft {
  const metadata =
    assignment?.metadata && typeof assignment.metadata === 'object' ? assignment.metadata : null;
  const primaryProcessIdRaw =
    typeof metadata?.primaryProcessId === 'string'
      ? metadata.primaryProcessId.trim()
      : '';
  const attachedProcessIds = toStringArray(metadata?.attachedProcessIds);
  const primaryProcessId = primaryProcessIdRaw || attachedProcessIds[0] || '';

  return {
    agentCode: cleanText(assignment?.agentCode) || PLATFORM_PROJECT_MANAGER_AGENT_CODE,
    active: Boolean(assignment?.active),
    primaryProcessId,
    clientScopes: normalizeProjectManagerClientScopes(metadata?.clientScopes),
    autonomyMode: readProjectManagerDefaultAutonomyMode(metadata),
    clientOverrides: readProjectManagerClientOverrides(metadata?.clientOverrides),
  };
}

function isPlanningAgentDefinition(agent: BrainAIAgentDefinition | null | undefined) {
  if (!agent) return false;
  const metadata = asObject(agent.metadata);
  const surface = cleanText(metadata?.surface);
  const planningRole = cleanText(metadata?.planningRole);
  return (
    surface === 'brain_project_manager' ||
    surface === 'brain_program_manager' ||
    planningRole === 'service_project_manager' ||
    planningRole === 'program_manager'
  );
}

function isSelectablePlanningAgentDefinition(
  agent: BrainAIAgentDefinition | null | undefined,
) {
  if (!isPlanningAgentDefinition(agent)) return false;
  const metadata = asObject(agent?.metadata);
  return metadata?.legacy !== true;
}

function isPlanningAgentAssignment(
  assignment: BrainAIAgentAssignment | null | undefined,
): assignment is BrainAIAgentAssignment {
  if (!assignment) return false;
  const surface = cleanText(assignment.agentSurface);
  const planningRole = cleanText(assignment.planningRole);
  return (
    surface === 'brain_project_manager' ||
    surface === 'brain_program_manager' ||
    planningRole === 'service_project_manager' ||
    planningRole === 'program_manager' ||
    SERVICE_PROJECT_MANAGER_AGENT_CODES.includes(cleanText(assignment.agentCode)) ||
    cleanText(assignment.agentCode) === PROGRAM_MANAGER_AGENT_CODE
  );
}

type PlanningNodeDomain =
  | 'platform'
  | 'assets_design'
  | 'marketing'
  | 'program'
  | null;

function inferPlanningNodeDomain(
  serviceCodeRaw: string,
  teamLabelRaw = '',
): PlanningNodeDomain {
  const serviceCode = cleanText(serviceCodeRaw).toLowerCase();
  if (serviceCode.includes('asset') || serviceCode.includes('design')) {
    return 'assets_design';
  }
  if (serviceCode.includes('marketing')) {
    return 'marketing';
  }
  if (serviceCode) {
    return 'platform';
  }
  const teamLabel = cleanText(teamLabelRaw).toLowerCase();
  if (
    teamLabel.includes('executive') ||
    teamLabel.includes('program') ||
    teamLabel.includes('operations')
  ) {
    return 'program';
  }
  return null;
}

function inferDefaultPlanningAgentCode(
  serviceCodeRaw: string,
  teamLabelRaw = '',
) {
  const domain = inferPlanningNodeDomain(serviceCodeRaw, teamLabelRaw);
  if (domain === 'assets_design') return DESIGN_PROJECT_MANAGER_AGENT_CODE;
  if (domain === 'marketing') return MARKETING_PROJECT_MANAGER_AGENT_CODE;
  if (domain === 'program') return PROGRAM_MANAGER_AGENT_CODE;
  if (domain === 'platform') return PLATFORM_PROJECT_MANAGER_AGENT_CODE;
  return '';
}

function planningAgentSupportsNodeDomain(
  agent: BrainAIAgentDefinition | null | undefined,
  domain: PlanningNodeDomain,
) {
  if (!isPlanningAgentDefinition(agent)) return false;
  const metadata = asObject((agent as BrainAIAgentDefinition).metadata);
  const surface = cleanText(metadata?.surface);
  const planningRole = cleanText(metadata?.planningRole);
  const planningDomain = cleanText(metadata?.planningDomain);
  if (domain === 'program') {
    return (
      surface === 'brain_program_manager' ||
      planningRole === 'program_manager'
    );
  }
  if (domain === 'assets_design' || domain === 'marketing' || domain === 'platform') {
    return (
      (surface === 'brain_project_manager' ||
        planningRole === 'service_project_manager') &&
      planningDomain === domain
    );
  }
  return false;
}

function mapAssignmentToAgentDefinition(
  assignment: BrainAIAgentAssignment | null,
): BrainAIAgentDefinition | null {
  if (!assignment) return null;
  return {
    id: cleanText(assignment.agentId),
    code: cleanText(assignment.agentCode),
    title: cleanText(assignment.agentTitle) || cleanText(assignment.agentCode),
    description: '',
    roleSummary: '',
    systemPrompt: '',
    defaultProvider: '',
    defaultModel: null,
    capabilities: [],
    allowedTools: [],
    requiresHumanApproval: true,
    status: 'active',
    metadata: assignment.agentMetadata,
  };
}

function projectManagerDraftEquals(
  left: ProjectManagerAssignmentDraft | null,
  right: ProjectManagerAssignmentDraft,
) {
  if (!left) return false;
  return (
    left.agentCode === right.agentCode &&
    left.active === right.active &&
    left.primaryProcessId === right.primaryProcessId &&
    left.autonomyMode === right.autonomyMode &&
    sameProjectManagerClientScopes(left.clientScopes, right.clientScopes) &&
    JSON.stringify(left.clientOverrides) === JSON.stringify(right.clientOverrides)
  );
}

function matchesProjectManagerProcessSearch(
  process: BrainKBaseProcessOption,
  query: string,
): boolean {
  const normalizedQuery = cleanText(query).toLowerCase();
  if (!normalizedQuery) return true;
  const haystack = [
    process.title,
    process.status,
    process.processType,
    process.serviceCode,
    process.serviceLabel,
    process.category,
    ...process.subcategories,
  ]
    .map((value) => cleanText(value).toLowerCase())
    .filter(Boolean)
    .join(' ');
  return haystack.includes(normalizedQuery);
}

function processMatchesRoutedService(
  process: BrainKBaseProcessOption,
  serviceCode: string,
  serviceTitle: string,
): boolean {
  const normalizedServiceCode = cleanText(serviceCode).toLowerCase();
  const normalizedServiceTitle = cleanText(serviceTitle).toLowerCase();
  if (!normalizedServiceCode && !normalizedServiceTitle) return false;

  const processServiceCode = cleanText(process.serviceCode).toLowerCase();
  if (normalizedServiceCode && processServiceCode === normalizedServiceCode) {
    return true;
  }

  const haystack = [
    process.title,
    process.serviceCode,
    process.serviceLabel,
    process.category,
    ...process.subcategories,
  ]
    .map((value) => cleanText(value).toLowerCase())
    .filter(Boolean)
    .join(' ');

  if (normalizedServiceTitle && haystack.includes(normalizedServiceTitle)) {
    return true;
  }

  return normalizedServiceCode ? haystack.includes(normalizedServiceCode) : false;
}

function parseNodeRoutingRule(rules: Record<string, unknown> | undefined): NodeRoutingRule | null {
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return null;
  const routingRaw = rules.routing;
  if (!routingRaw || typeof routingRaw !== 'object' || Array.isArray(routingRaw)) return null;
  const routing = routingRaw as Record<string, unknown>;
  const serviceCode = typeof routing.serviceCode === 'string' ? routing.serviceCode.trim() : '';
  if (!serviceCode) return null;
  const tierSkuIdRaw = typeof routing.tierSkuId === 'string' ? routing.tierSkuId.trim() : '';
  return {
    serviceCode,
    tierSkuId: tierSkuIdRaw || null,
  };
}

const DEFAULT_TEAM_CAPACITY_HOURS = 30;

function readTeamSettings(rules: Record<string, unknown> | undefined): TeamSettingsDraft {
  const base: TeamSettingsDraft = {
    roundRobinEnabled: false,
    teamCapacityHours: DEFAULT_TEAM_CAPACITY_HOURS,
  };
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return base;
  const teamRaw = rules.teamSettings;
  if (!teamRaw || typeof teamRaw !== 'object' || Array.isArray(teamRaw)) return base;
  const team = teamRaw as Record<string, unknown>;
  return {
    roundRobinEnabled:
      typeof team.roundRobinEnabled === 'boolean'
        ? team.roundRobinEnabled
        : base.roundRobinEnabled,
    teamCapacityHours: Math.max(
      1,
      Math.min(200, Math.round(Number(team.teamCapacityHours) || base.teamCapacityHours)),
    ),
  };
}

function formatHoursLabel(hours: number): string {
  if (!Number.isFinite(hours)) return '--';
  return `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h`;
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function formatDateTimeLabel(value: string | null | undefined): string {
  const normalized = cleanText(value);
  if (!normalized) return '--';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '--';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getProjectManagerProposal(run: BrainProjectManagerRun): Record<string, unknown> {
  const approvalProposal = asObject(run.approval?.proposal);
  if (approvalProposal) return approvalProposal;
  return asObject(asObject(run.responsePayload)?.proposal) ?? {};
}

function getProjectManagerWarnings(run: BrainProjectManagerRun): string[] {
  return toStringArray(getProjectManagerProposal(run).warnings);
}

function getProjectManagerDecisionLog(run: BrainProjectManagerRun): string[] {
  return toStringArray(getProjectManagerProposal(run).decisionLog);
}

function getProjectManagerGuidanceRequests(
  run: BrainProjectManagerRun,
): Array<{ title: string; question: string; reason: string | null }> {
  const raw = getProjectManagerProposal(run).guidanceRequests;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const record = asObject(entry);
      const question = cleanText(record?.question);
      if (!question) return null;
      return {
        title: cleanText(record?.title) || 'Guidance needed',
        question,
        reason: cleanText(record?.reason) || null,
      };
    })
    .filter(
      (
        entry,
      ): entry is { title: string; question: string; reason: string | null } =>
        Boolean(entry),
    );
}

function getProjectManagerScopeValue(
  run: BrainProjectManagerRun,
  key: 'processTitle' | 'projectId' | 'nodeId',
): string {
  const proposal = getProjectManagerProposal(run);
  const scope = asObject(proposal.scope);
  return cleanText(scope?.[key]);
}

function getProjectManagerPromptInstruction(run: BrainProjectManagerRun): string {
  const promptSnapshot = asObject(run.promptSnapshot);
  const scope = asObject(promptSnapshot?.scope);
  return cleanText(scope?.instruction);
}

function findInheritedRoutingRule(
  nodeId: string,
  nodes: SkillsTreeNode[],
): InheritedNodeRoutingRule | null {
  const nodeMap = new Map<string, SkillsTreeNode>();
  for (const node of nodes) {
    nodeMap.set(node.id, node);
  }
  let cursor = nodeMap.get(nodeId)?.parentId ?? null;
  const visited = new Set<string>();

  while (cursor) {
    if (visited.has(cursor)) break;
    visited.add(cursor);
    const current = nodeMap.get(cursor);
    if (!current) break;
    const routing = parseNodeRoutingRule(current.rules);
    if (routing) {
      return {
        ...routing,
        sourceNodeId: current.id,
        sourceNodeTitle: current.title,
      };
    }
    cursor = current.parentId;
  }
  return null;
}

export function NodeDetailsDrawer({
  node,
  allNodes,
  open,
  operatorOptions,
  serviceRoutingOptions,
  serviceRoutingOptionsLoading,
  operatorsLoading,
  canEdit,
  onClose,
  onTitleChange,
  onMetaChange,
  onRulesChange,
}: NodeDetailsDrawerProps) {
  const [tab, setTab] = useState<DrawerTabKey>('overview');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftPosition, setDraftPosition] = useState('');
  const [draftTeam, setDraftTeam] = useState('');
  const [draftDesignation, setDraftDesignation] = useState('');
  const [draftChildDesignation, setDraftChildDesignation] = useState('');
  const [draftAppointedAgent, setDraftAppointedAgent] = useState('');
  const [draftAppointedAgentUid, setDraftAppointedAgentUid] = useState('');
  const [draftAppointedAgentEmail, setDraftAppointedAgentEmail] = useState('');
  const [draftAppointedAgentAvatarUrl, setDraftAppointedAgentAvatarUrl] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [childDesignationInput, setChildDesignationInput] = useState('');
  const [draftRoutingServiceCode, setDraftRoutingServiceCode] = useState('');
  const [draftRoutingTierSkuId, setDraftRoutingTierSkuId] = useState('');
  const [projectManagerAgent, setProjectManagerAgent] = useState<BrainAIAgentDefinition | null>(null);
  const [projectManagerAgentOptions, setProjectManagerAgentOptions] = useState<
    BrainAIAgentDefinition[]
  >([]);
  const [projectManagerAssignments, setProjectManagerAssignments] = useState<
    BrainAIAgentAssignment[]
  >([]);
  const [projectManagerAssignment, setProjectManagerAssignment] = useState<BrainAIAgentAssignment | null>(null);
  const [projectManagerProcesses, setProjectManagerProcesses] = useState<BrainKBaseProcessOption[]>([]);
  const [projectManagerDraft, setProjectManagerDraft] = useState<ProjectManagerAssignmentDraft>({
    agentCode: PLATFORM_PROJECT_MANAGER_AGENT_CODE,
    active: false,
    primaryProcessId: '',
    clientScopes: [...DEFAULT_PROJECT_MANAGER_CLIENT_SCOPES],
    autonomyMode: 'autonomous',
    clientOverrides: [],
  });
  const [projectManagerBaseline, setProjectManagerBaseline] =
    useState<ProjectManagerAssignmentDraft | null>(null);
  const [projectManagerLoadedNodeId, setProjectManagerLoadedNodeId] = useState<string | null>(null);
  const [projectManagerAccordionOpen, setProjectManagerAccordionOpen] = useState(false);
  const [projectManagerVisibleAgentCodes, setProjectManagerVisibleAgentCodes] = useState<string[]>([]);
  const [projectManagerPickerAgentCode, setProjectManagerPickerAgentCode] =
    useState(PLATFORM_PROJECT_MANAGER_AGENT_CODE);
  const [projectManagerOpenAgentCode, setProjectManagerOpenAgentCode] = useState<string | null>(null);
  const [projectManagerLoading, setProjectManagerLoading] = useState(false);
  const [projectManagerSaving, setProjectManagerSaving] = useState(false);
  const [projectManagerError, setProjectManagerError] = useState<string | null>(null);
  const [projectManagerProcessSearch, setProjectManagerProcessSearch] = useState('');
  const [projectManagerProcessPickerOpen, setProjectManagerProcessPickerOpen] = useState(false);
  const [projectManagerViewTab, setProjectManagerViewTab] =
    useState<ProjectManagerViewTab>('config');
  const [projectManagerRuns, setProjectManagerRuns] = useState<BrainProjectManagerRun[]>([]);
  const [projectManagerRunsLoading, setProjectManagerRunsLoading] = useState(false);
  const [projectManagerRunsError, setProjectManagerRunsError] = useState<string | null>(null);
  const [projectManagerRunInstruction, setProjectManagerRunInstruction] = useState('');
  const [projectManagerRunProjectId, setProjectManagerRunProjectId] = useState('');
  const [projectManagerRunPath, setProjectManagerRunPath] = useState<
    '' | 'new_project' | 'existing_project'
  >('');
  const [projectManagerRunResourceMode, setProjectManagerRunResourceMode] = useState<
    'all_resources' | 'specific_resources'
  >('all_resources');
  const [projectManagerRunSaving, setProjectManagerRunSaving] = useState(false);
  const [projectManagerApprovalActionId, setProjectManagerApprovalActionId] = useState<
    string | null
  >(null);
  const [projectManagerNodeProjects, setProjectManagerNodeProjects] = useState<
    ProjectManagerNodeProjectOption[]
  >([]);
  const [projectManagerClients, setProjectManagerClients] = useState<
    ProjectManagerClientAccount[]
  >([]);
  const [projectManagerClientsLoading, setProjectManagerClientsLoading] = useState(false);
  const [projectManagerClientsError, setProjectManagerClientsError] = useState<string | null>(
    null,
  );
  const [projectManagerClientSearch, setProjectManagerClientSearch] = useState('');
  const [projectManagerRunOrgId, setProjectManagerRunOrgId] = useState('');
  const [projectManagerLogOrgFilter, setProjectManagerLogOrgFilter] = useState('');
  const [projectManagerReviewNotes, setProjectManagerReviewNotes] = useState<
    Record<string, string>
  >({});
  const [projectManagerExpandedClientId, setProjectManagerExpandedClientId] = useState<string | null>(
    null,
  );
  const [, setProjectManagerScopeDrawerOrgId] = useState<string | null>(null);
  const [, setProjectManagerConversationDrawerOrgId] = useState<string | null>(null);
  const [projectManagerGlobalScopeDrawerOpen, setProjectManagerGlobalScopeDrawerOpen] =
    useState(false);
  const [projectManagerDeleteTarget, setProjectManagerDeleteTarget] = useState<{
    assignmentId: string;
    agentCode: string;
    agentTitle: string;
  } | null>(null);
  const [projectManagerDeleting, setProjectManagerDeleting] = useState(false);
  const [teamSettingsDraft, setTeamSettingsDraft] = useState<TeamSettingsDraft>({
    roundRobinEnabled: false,
    teamCapacityHours: DEFAULT_TEAM_CAPACITY_HOURS,
  });
  const [teamSettingsBaseline, setTeamSettingsBaseline] = useState<TeamSettingsDraft>({
    roundRobinEnabled: false,
    teamCapacityHours: DEFAULT_TEAM_CAPACITY_HOURS,
  });
  const [teamCapacitySummary, setTeamCapacitySummary] = useState<TeamCapacitySummary | null>(
    null,
  );
  const [teamTicketsLoading, setTeamTicketsLoading] = useState(false);
  const [teamTicketsError, setTeamTicketsError] = useState<string | null>(null);
  const [teamRosterPickerOpen, setTeamRosterPickerOpen] = useState(false);
  const [teamRosterSavingUid, setTeamRosterSavingUid] = useState<string | null>(null);
  const [teamRosterError, setTeamRosterError] = useState<string | null>(null);
  const [routingValidationMessage, setRoutingValidationMessage] = useState<string | null>(null);
  const agentPickerRef = useRef<HTMLDivElement | null>(null);
  const projectManagerProcessPickerRef = useRef<HTMLDivElement | null>(null);
  const teamRosterPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (agentPickerOpen && !agentPickerRef.current?.contains(target)) {
        setAgentPickerOpen(false);
      }
      if (
        projectManagerProcessPickerOpen &&
        !projectManagerProcessPickerRef.current?.contains(target)
      ) {
        setProjectManagerProcessPickerOpen(false);
      }
      if (teamRosterPickerOpen && !teamRosterPickerRef.current?.contains(target)) {
        setTeamRosterPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [agentPickerOpen, projectManagerProcessPickerOpen, teamRosterPickerOpen]);

  useEffect(() => {
    setDraftTitle(node?.title ?? '');
    setDraftPosition(toMetaText(node?.meta?.position));
    setDraftTeam(toMetaText(node?.meta?.team));
    setDraftDesignation(toMetaText(node?.meta?.designation));
    setDraftChildDesignation(toMetaText(node?.meta?.childDesignation));
    setDraftAppointedAgent(toMetaText(node?.meta?.appointedAgent));
    setDraftAppointedAgentUid(toMetaText(node?.meta?.appointedAgentUid));
    setDraftAppointedAgentEmail(toMetaText(node?.meta?.appointedAgentEmail));
    setDraftAppointedAgentAvatarUrl(toMetaText(node?.meta?.appointedAgentAvatarUrl));
    setAgentSearch(toMetaText(node?.meta?.appointedAgent));
    setAgentPickerOpen(false);
    setChildDesignationInput('');
    const currentRouting = parseNodeRoutingRule(node?.rules);
    setDraftRoutingServiceCode(currentRouting?.serviceCode ?? '');
    setDraftRoutingTierSkuId(currentRouting?.tierSkuId ?? '');
    setProjectManagerAgent(null);
    setProjectManagerAssignment(null);
    setProjectManagerProcesses([]);
    setProjectManagerAgentOptions([]);
    setProjectManagerAssignments([]);
    setProjectManagerDeleteTarget(null);
    const defaultPlanningAgentCode = inferDefaultPlanningAgentCode(
      currentRouting?.serviceCode ?? '',
      toMetaText(node?.meta?.team),
    );
    setProjectManagerDraft({
      agentCode: defaultPlanningAgentCode,
      active: false,
      primaryProcessId: '',
      clientScopes: [...DEFAULT_PROJECT_MANAGER_CLIENT_SCOPES],
      autonomyMode: 'autonomous',
      clientOverrides: [],
    });
    setProjectManagerBaseline(null);
    setProjectManagerLoadedNodeId(null);
    setProjectManagerVisibleAgentCodes([]);
    setProjectManagerPickerAgentCode(defaultPlanningAgentCode);
    setProjectManagerOpenAgentCode(null);
    setProjectManagerLoading(false);
    setProjectManagerSaving(false);
    setProjectManagerError(null);
    setProjectManagerProcessSearch('');
    setProjectManagerProcessPickerOpen(false);
    setProjectManagerViewTab('config');
    setProjectManagerRuns([]);
    setProjectManagerRunsLoading(false);
    setProjectManagerRunsError(null);
    setProjectManagerRunInstruction('');
    setProjectManagerRunProjectId('');
    setProjectManagerRunPath('new_project');
    setProjectManagerRunResourceMode('all_resources');
    setProjectManagerRunSaving(false);
    setProjectManagerApprovalActionId(null);
    setProjectManagerNodeProjects([]);
    setProjectManagerClients([]);
    setProjectManagerClientsLoading(false);
    setProjectManagerClientsError(null);
    setProjectManagerClientSearch('');
    setProjectManagerRunOrgId('');
    setProjectManagerLogOrgFilter('');
    setProjectManagerReviewNotes({});
    setProjectManagerExpandedClientId(null);
    setProjectManagerScopeDrawerOrgId(null);
    const nextTeamSettings = readTeamSettings(
      node?.rules && typeof node.rules === 'object' && !Array.isArray(node.rules)
        ? (node.rules as Record<string, unknown>)
        : undefined,
    );
    setTeamSettingsDraft(nextTeamSettings);
    setTeamSettingsBaseline(nextTeamSettings);
    setTeamCapacitySummary(null);
    setTeamTicketsLoading(false);
    setTeamTicketsError(null);
    setTeamRosterPickerOpen(false);
    setTeamRosterSavingUid(null);
    setTeamRosterError(null);
    setRoutingValidationMessage(null);
  }, [node?.id, node?.title, node?.meta, node?.rules]);

  useEffect(() => {
    setTab('overview');
  }, [node?.id]);

  const persistMeta = (nextMeta: NodeMetaDraft) => {
    if (!node || !canEdit) return;
    const normalizedRules = parseChildDesignationRules(nextMeta.childDesignation);
    const normalized: NodeMetaDraft = {
      position: nextMeta.position.trim(),
      team: nextMeta.team.trim(),
      designation: singularizeDesignation(nextMeta.designation),
      childDesignation: serializeChildDesignationRules(normalizedRules),
      appointedAgent: nextMeta.appointedAgent.trim(),
      appointedAgentUid: nextMeta.appointedAgentUid.trim(),
      appointedAgentEmail: nextMeta.appointedAgentEmail.trim(),
      appointedAgentAvatarUrl: nextMeta.appointedAgentAvatarUrl.trim(),
    };

    setDraftPosition(normalized.position);
    setDraftTeam(normalized.team);
    setDraftDesignation(normalized.designation);
    setDraftChildDesignation(normalized.childDesignation);
    setDraftAppointedAgent(normalized.appointedAgent);
    setDraftAppointedAgentUid(normalized.appointedAgentUid);
    setDraftAppointedAgentEmail(normalized.appointedAgentEmail);
    setDraftAppointedAgentAvatarUrl(normalized.appointedAgentAvatarUrl);
    setAgentSearch(normalized.appointedAgent);
    setRoutingValidationMessage(null);

    onMetaChange(node.id, normalized);
  };

  const clearAppointedAgentAssignment = () => {
    if (!canEdit) return;
    setAgentPickerOpen(false);
    persistMeta({
      position: draftPosition,
      team: draftTeam,
      designation: draftDesignation,
      childDesignation: draftChildDesignation,
      appointedAgent: '',
      appointedAgentUid: '',
      appointedAgentEmail: '',
      appointedAgentAvatarUrl: '',
    });
  };

  const matchingOperators = useMemo(() => {
    const query = agentSearch.trim().toLowerCase();
    if (!query) return operatorOptions;
    return operatorOptions.filter((operator) => {
      const haystack = `${operator.name} ${operator.email} ${operator.role}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [agentSearch, operatorOptions]);

  const childDesignationRules = useMemo(
    () => parseChildDesignationRules(draftChildDesignation),
    [draftChildDesignation],
  );

  const localRoutingRule = useMemo(
    () => (node ? parseNodeRoutingRule(node.rules) : null),
    [node],
  );

  const inheritedRoutingRule = useMemo(() => {
    if (!node || localRoutingRule) return null;
    return findInheritedRoutingRule(node.id, allNodes);
  }, [allNodes, localRoutingRule, node]);

  const effectiveRoutingServiceCode = localRoutingRule
    ? draftRoutingServiceCode
    : draftRoutingServiceCode || inheritedRoutingRule?.serviceCode || '';
  const effectiveRoutingTierSkuId = localRoutingRule
    ? draftRoutingTierSkuId
    : draftRoutingTierSkuId || inheritedRoutingRule?.tierSkuId || '';
  const persistedTeamLabel = toMetaText(node?.meta?.team);
  const localRoutingMissingTeamLabel =
    Boolean(draftRoutingServiceCode.trim()) && !persistedTeamLabel.trim();
  const effectivePlanningNodeDomain = useMemo(
    () => inferPlanningNodeDomain(effectiveRoutingServiceCode, draftTeam),
    [draftTeam, effectiveRoutingServiceCode],
  );

  const serviceRoutingPicklistOptions = useMemo<Array<GlassPicklistOption<string>>>(
    () => [
      { value: '', label: '-- No service routing --' },
      ...serviceRoutingOptions.map((service) => ({
        value: service.serviceCode,
        label: `${service.serviceTitle} (${service.serviceCode})`,
      })),
    ],
    [serviceRoutingOptions],
  );

  const selectedRoutingService = useMemo(
    () =>
      serviceRoutingOptions.find(
        (service) => service.serviceCode === effectiveRoutingServiceCode,
      ) ?? null,
    [effectiveRoutingServiceCode, serviceRoutingOptions],
  );

  const tierRoutingPicklistOptions = useMemo<Array<GlassPicklistOption<string>>>(() => {
    if (!selectedRoutingService) {
      return [{ value: '', label: '-- Select service first --' }];
    }
    return [
      { value: '', label: '-- Service-level routing (all tiers) --' },
      ...selectedRoutingService.tiers.map((tier) => ({
        value: tier.skuId,
        label: `${tier.name} (${tier.skuId})`,
      })),
    ];
  }, [selectedRoutingService]);

  const selectedRoutingTier = useMemo(
    () =>
      selectedRoutingService?.tiers.find((tier) => tier.skuId === effectiveRoutingTierSkuId) ?? null,
    [effectiveRoutingTierSkuId, selectedRoutingService],
  );

  const sortedProjectManagerProcesses = useMemo(() => {
    const serviceCode = cleanText(effectiveRoutingServiceCode);
    const serviceTitle = cleanText(selectedRoutingService?.serviceTitle);
    return [...projectManagerProcesses].sort((left, right) => {
      const leftMatches = processMatchesRoutedService(left, serviceCode, serviceTitle) ? 1 : 0;
      const rightMatches = processMatchesRoutedService(right, serviceCode, serviceTitle) ? 1 : 0;
      if (leftMatches !== rightMatches) return rightMatches - leftMatches;
      return left.title.localeCompare(right.title);
    });
  }, [effectiveRoutingServiceCode, projectManagerProcesses, selectedRoutingService?.serviceTitle]);

  const suggestedProjectManagerProcesses = useMemo(() => {
    const serviceCode = cleanText(effectiveRoutingServiceCode);
    const serviceTitle = cleanText(selectedRoutingService?.serviceTitle);
    const matching = sortedProjectManagerProcesses.filter((process) =>
      processMatchesRoutedService(process, serviceCode, serviceTitle),
    );
    return matching.length ? matching : sortedProjectManagerProcesses;
  }, [effectiveRoutingServiceCode, selectedRoutingService?.serviceTitle, sortedProjectManagerProcesses]);

  const searchedProjectManagerProcesses = useMemo(
    () =>
      sortedProjectManagerProcesses.filter((process) =>
        matchesProjectManagerProcessSearch(process, projectManagerProcessSearch),
      ),
    [projectManagerProcessSearch, sortedProjectManagerProcesses],
  );

  const selectedProjectManagerProcess = useMemo(
    () =>
      projectManagerProcesses.find(
        (process) => process.id === projectManagerDraft.primaryProcessId,
      ) ?? null,
    [projectManagerDraft.primaryProcessId, projectManagerProcesses],
  );

  const projectManagerDirty = useMemo(
    () => !projectManagerDraftEquals(projectManagerBaseline, projectManagerDraft),
    [projectManagerBaseline, projectManagerDraft],
  );

  const teamSettingsDirty = useMemo(
    () =>
      teamSettingsBaseline.roundRobinEnabled !== teamSettingsDraft.roundRobinEnabled ||
      teamSettingsBaseline.teamCapacityHours !== teamSettingsDraft.teamCapacityHours,
    [teamSettingsBaseline, teamSettingsDraft],
  );

  const canRunProjectManager =
    projectManagerDraft.active && Boolean(projectManagerDraft.primaryProcessId.trim());

  const selectedProjectManagerAgent = useMemo(
    () =>
      projectManagerAgentOptions.find(
        (agent) => cleanText(agent.code) === cleanText(projectManagerDraft.agentCode),
      ) ?? projectManagerAgent,
    [projectManagerAgent, projectManagerAgentOptions, projectManagerDraft.agentCode],
  );

  const compatibleProjectManagerAgentOptions = useMemo(
    () =>
      projectManagerAgentOptions.filter((agent) =>
        planningAgentSupportsNodeDomain(agent, effectivePlanningNodeDomain),
      ),
    [effectivePlanningNodeDomain, projectManagerAgentOptions],
  );

  const availableProjectManagerAgentOptions = useMemo(
    () =>
      compatibleProjectManagerAgentOptions.filter(
        (agent) => !projectManagerVisibleAgentCodes.includes(cleanText(agent.code)),
      ),
    [compatibleProjectManagerAgentOptions, projectManagerVisibleAgentCodes],
  );

  const renderedProjectManagerAgentCodes = useMemo(
    () =>
      projectManagerVisibleAgentCodes.length
        ? projectManagerVisibleAgentCodes
        : uniqueStrings(
            projectManagerAssignments.map((entry) => cleanText(entry.agentCode)),
          ),
    [projectManagerAssignments, projectManagerVisibleAgentCodes],
  );

  useEffect(() => {
    if (!availableProjectManagerAgentOptions.length) return;
    if (
      availableProjectManagerAgentOptions.some(
        (agent) => cleanText(agent.code) === cleanText(projectManagerPickerAgentCode),
      )
    ) {
      return;
    }
    setProjectManagerPickerAgentCode(
      cleanText(availableProjectManagerAgentOptions[0]?.code) ||
        inferDefaultPlanningAgentCode(effectiveRoutingServiceCode, draftTeam),
    );
  }, [
    availableProjectManagerAgentOptions,
    draftTeam,
    effectiveRoutingServiceCode,
    projectManagerPickerAgentCode,
  ]);

  const projectManagerPendingRuns = useMemo(
    () =>
      projectManagerRuns.filter((run) => {
        const status = cleanText(run.approval?.status).toLowerCase();
        return status === 'pending' || status === 'approved';
      }),
    [projectManagerRuns],
  );

  const projectManagerClientMap = useMemo(() => {
    const map = new Map<string, ProjectManagerClientAccount>();
    projectManagerClients.forEach((client) => {
      map.set(client.id, client);
    });
    projectManagerNodeProjects.forEach((project) => {
      if (!project.orgId) return;
      if (!map.has(project.orgId)) {
        map.set(project.orgId, {
          id: project.orgId,
          name: project.orgName || 'Client account',
          ownerEmail: null,
          assignedServiceTeam: null,
          assignedServiceTeams: [],
          primaryServiceActive: null,
          serviceLabels: [],
          projectCountOnNode: 1,
          latestProjectUpdatedAt: null,
          latestRunAt: null,
          latestSubscriptionUpdatedAt: null,
          sources: ['project'],
          source: 'project',
        });
      }
    });
    projectManagerRuns.forEach((run) => {
      const orgId = cleanText(run.orgId);
      if (!orgId || map.has(orgId)) return;
      map.set(orgId, {
        id: orgId,
        name: cleanText(run.orgName) || 'Client account',
        ownerEmail: null,
        assignedServiceTeam: null,
        assignedServiceTeams: [],
        primaryServiceActive: null,
        serviceLabels: [],
        projectCountOnNode: 0,
        latestProjectUpdatedAt: null,
        latestRunAt: run.createdAt || null,
        latestSubscriptionUpdatedAt: null,
        sources: ['run'],
        source: 'run',
      });
    });
    return map;
  }, [projectManagerClients, projectManagerNodeProjects, projectManagerRuns]);

  const projectManagerClientOptions = useMemo(
    () => {
      const projectOrgIds = new Set(projectManagerNodeProjects.map((project) => project.orgId));
      const runOrgIds = new Set(
        projectManagerRuns.map((run) => cleanText(run.orgId)).filter(Boolean),
      );
      const nodeTitle = cleanText(node?.title).toLowerCase();
      const allClients = Array.from(projectManagerClientMap.values());
      const relevantClients = allClients.filter((client) => {
        if (projectOrgIds.has(client.id) || runOrgIds.has(client.id)) return true;
        if (!nodeTitle) return false;
        const labels = [client.assignedServiceTeam, ...client.assignedServiceTeams]
          .map((value) => cleanText(value).toLowerCase())
          .filter(Boolean);
        return labels.some((label) => label.includes(nodeTitle));
      });
      const source = relevantClients.length ? relevantClients : allClients;
      return source.sort((left, right) => left.name.localeCompare(right.name));
    },
    [node?.title, projectManagerClientMap, projectManagerNodeProjects, projectManagerRuns],
  );

  const projectManagerClientOverridesByOrgId = useMemo(
    () =>
      new Map(
        projectManagerDraft.clientOverrides.map((entry) => [entry.orgId, entry]),
      ),
    [projectManagerDraft.clientOverrides],
  );

  const filteredProjectManagerClients = useMemo(() => {
    const query = cleanText(projectManagerClientSearch).toLowerCase();
    if (!query) return projectManagerClientOptions;
    return projectManagerClientOptions.filter((client) => {
      const haystack = [
        client.name,
        client.ownerEmail,
        client.assignedServiceTeam,
        ...client.assignedServiceTeams,
      ]
        .map((value) => cleanText(value).toLowerCase())
        .filter(Boolean)
        .join(' ');
      return haystack.includes(query);
    });
  }, [projectManagerClientOptions, projectManagerClientSearch]);

  const filteredProjectManagerRuns = useMemo(() => {
    const filterOrgId = cleanText(projectManagerLogOrgFilter);
    if (!filterOrgId) return projectManagerRuns;
    return projectManagerRuns.filter((run) => cleanText(run.orgId) === filterOrgId);
  }, [projectManagerLogOrgFilter, projectManagerRuns]);

  const getProjectManagerClientEffectiveMode = useCallback(
    (orgId: string) =>
      projectManagerClientOverridesByOrgId.get(orgId)?.autonomyMode ?? projectManagerDraft.autonomyMode,
    [projectManagerClientOverridesByOrgId, projectManagerDraft.autonomyMode],
  );

  const getProjectManagerClientScopes = useCallback(
    (orgId: string) =>
      projectManagerClientOverridesByOrgId.get(orgId)?.scopes ??
      projectManagerDraft.clientScopes,
    [projectManagerClientOverridesByOrgId, projectManagerDraft.clientScopes],
  );

  const projectManagerGlobalScopeValues = useMemo(
    () => normalizeProjectManagerClientScopes(projectManagerDraft.clientScopes),
    [projectManagerDraft.clientScopes],
  );

  const updateProjectManagerClientOverride = useCallback(
    (
      orgIdRaw: string,
      patch:
        | {
            autonomyMode?: 'human_in_loop' | 'autonomous';
            scopes?: string[];
          }
        | null,
    ) => {
      const orgId = cleanText(orgIdRaw);
      if (!orgId) return;
      setProjectManagerDraft((previous) => {
        const existing = previous.clientOverrides.find((entry) => entry.orgId === orgId) ?? null;
        const preserved = previous.clientOverrides.filter((entry) => entry.orgId !== orgId);
        if (!patch) {
          return {
            ...previous,
            clientOverrides: preserved,
          };
        }
        const nextOverride: ProjectManagerClientOverride = {
          orgId,
          autonomyMode:
            patch.autonomyMode ??
            existing?.autonomyMode ??
            previous.autonomyMode,
          scopes: Array.isArray(patch.scopes)
            ? normalizeProjectManagerClientScopes(patch.scopes)
            : existing?.scopes ?? null,
        };
        const shouldPersist =
          nextOverride.autonomyMode !== previous.autonomyMode ||
          (Array.isArray(nextOverride.scopes) &&
            !sameProjectManagerClientScopes(
              nextOverride.scopes,
              previous.clientScopes,
            ));
        return {
          ...previous,
          clientOverrides: shouldPersist
            ? [...preserved, nextOverride].sort((left, right) =>
                left.orgId.localeCompare(right.orgId),
              )
            : preserved,
        };
      });
    },
    [],
  );

  const teamMemberSnapshots = useMemo<TeamMemberCapacitySnapshot[]>(() => {
    const contributors = teamCapacitySummary?.contributors ?? [];
    return contributors
      .map((member) => {
        const loadHours = member.remainingEstimateMinutes / 60;
        const ratio =
          teamSettingsDraft.teamCapacityHours > 0
            ? Math.min(1, loadHours / teamSettingsDraft.teamCapacityHours)
            : 0;
        return {
          operator: {
            id: member.uid,
            email: member.email ?? '',
            name: member.name,
            avatarUrl: member.photoURL ?? undefined,
            role: member.role === 'admin' ? 'admin' : 'agent',
          } as SkillsTreeOperator,
          assignedTicketCount: member.assignedTicketCount,
          remainingEstimateMinutes: member.remainingEstimateMinutes,
          loadHours,
          capacityRatio: ratio,
          remainingCapacityHours: Math.max(0, teamSettingsDraft.teamCapacityHours - loadHours),
          membershipRole: member.membershipRole,
          isPrimaryMembership: member.isPrimaryMembership,
          isRosterMember: member.isRosterMember,
          isOutsideRoster: member.isOutsideRoster,
        };
      })
      .sort(
        (left, right) =>
          right.loadHours - left.loadHours || left.operator.name.localeCompare(right.operator.name),
      );
  }, [teamCapacitySummary?.contributors, teamSettingsDraft.teamCapacityHours]);

  const loadTeamCapacitySummary = useCallback(
    async (teamNodeId: string) => {
      setTeamTicketsLoading(true);
      setTeamTicketsError(null);
      try {
        const payload = await apiFetch<any>(
          `/api/projects/team-capacity-summary?teamNodeId=${encodeURIComponent(teamNodeId)}`,
        );
        setTeamCapacitySummary(normalizeTeamCapacitySummaryPayload(payload));
      } catch (error) {
        setTeamTicketsError(toErrorMessage(error));
      } finally {
        setTeamTicketsLoading(false);
      }
    },
    [],
  );

  const loadProjectManagerRuns = useCallback(async (nodeId: string) => {
    const normalizedNodeId = cleanText(nodeId);
    if (!normalizedNodeId) {
      setProjectManagerRuns([]);
      return;
    }
    setProjectManagerRunsLoading(true);
    setProjectManagerRunsError(null);
    try {
      const runs = await listProjectManagerRuns({
        nodeId: normalizedNodeId,
        limit: 30,
      });
      setProjectManagerRuns(runs);
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    } finally {
      setProjectManagerRunsLoading(false);
    }
  }, []);

  const loadProjectManagerNodeProjects = useCallback(async (nodeId: string) => {
    const normalizedNodeId = cleanText(nodeId);
    if (!normalizedNodeId) {
      setProjectManagerNodeProjects([]);
      return;
    }
    try {
      const response = await apiFetch<{ projects?: Array<Record<string, unknown>> }>(
        `/api/projects?teamNodeId=${encodeURIComponent(normalizedNodeId)}`,
      );
      const nextProjects = (Array.isArray(response?.projects) ? response.projects : [])
        .map((project) => {
          const id = cleanText(project.id);
          const name = cleanText(project.name);
          const orgId = cleanText(project.orgId);
          if (!id || !name || !orgId) return null;
          return {
            id,
            name,
            orgId,
            orgName: cleanText(project.orgName) || '',
          };
        })
        .filter((project): project is ProjectManagerNodeProjectOption => Boolean(project))
        .sort((left, right) => left.name.localeCompare(right.name));
      setProjectManagerNodeProjects(nextProjects);
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    }
  }, []);

  const loadProjectManagerClients = useCallback(async (nodeId: string) => {
    const normalizedNodeId = cleanText(nodeId);
    if (!normalizedNodeId) {
      setProjectManagerClients([]);
      return;
    }
    setProjectManagerClientsLoading(true);
    setProjectManagerClientsError(null);
    try {
      const nextClients = (await fetchProjectManagerNodeClients(normalizedNodeId))
        .map((entry) => ({
          id: entry.orgId,
          name: entry.name,
          ownerEmail: entry.ownerEmail,
          assignedServiceTeam: entry.assignedServiceTeam,
          assignedServiceTeams: entry.assignedServiceTeams,
          primaryServiceActive: entry.primaryServiceActive,
          serviceLabels: entry.serviceLabels,
          projectCountOnNode: entry.projectCountOnNode,
          latestProjectUpdatedAt: entry.latestProjectUpdatedAt,
          latestRunAt: entry.latestRunAt,
          latestSubscriptionUpdatedAt: entry.latestSubscriptionUpdatedAt,
          sources: entry.sources,
          source: 'organization' as const,
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
      setProjectManagerClients(nextClients);
    } catch (error) {
      setProjectManagerClientsError(toErrorMessage(error));
    } finally {
      setProjectManagerClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !node || tab !== 'people') return;
    void loadTeamCapacitySummary(node.id);
  }, [loadTeamCapacitySummary, node, open, tab]);

  useEffect(() => {
    if (!open || !node || tab !== 'ai_agent') return;
    void loadProjectManagerRuns(node.id);
    void loadProjectManagerNodeProjects(node.id);
    void loadProjectManagerClients(node.id);
  }, [loadProjectManagerClients, loadProjectManagerNodeProjects, loadProjectManagerRuns, node, open, tab]);

  useEffect(() => {
    if (open && tab === 'ai_agent') return;
    setProjectManagerScopeDrawerOrgId(null);
    setProjectManagerConversationDrawerOrgId(null);
    setProjectManagerGlobalScopeDrawerOpen(false);
  }, [open, tab]);

  useEffect(() => {
    const selectedProject = projectManagerNodeProjects.find(
      (project) => project.id === projectManagerRunProjectId,
    );
    if (selectedProject) {
      setProjectManagerRunOrgId(selectedProject.orgId);
    }
  }, [projectManagerNodeProjects, projectManagerRunProjectId]);

  useEffect(() => {
    if (!projectManagerExpandedClientId || !projectManagerRunProjectId) return;
    const selectedProject = projectManagerNodeProjects.find(
      (project) => project.id === projectManagerRunProjectId,
    );
    if (selectedProject && selectedProject.orgId !== projectManagerExpandedClientId) {
      setProjectManagerRunProjectId('');
    }
  }, [projectManagerExpandedClientId, projectManagerNodeProjects, projectManagerRunProjectId]);

  useEffect(() => {
    if (projectManagerRunPath === 'existing_project') return;
    if (!projectManagerRunProjectId) return;
    setProjectManagerRunProjectId('');
  }, [projectManagerRunPath, projectManagerRunProjectId]);

  const rosterMemberUidSet = useMemo(
    () =>
      new Set(
        teamMemberSnapshots
          .filter((member) => member.isRosterMember)
          .map((member) => member.operator.id),
      ),
    [teamMemberSnapshots],
  );

  const availableRosterOperators = useMemo(
    () =>
      operatorOptions
        .filter((operator) => !rosterMemberUidSet.has(operator.id))
        .sort((left, right) => left.name.localeCompare(right.name)),
    [operatorOptions, rosterMemberUidSet],
  );

  useEffect(() => {
    if (!open || !node || tab !== 'ai_agent') return;
    if (projectManagerLoadedNodeId === node.id) return;

    let active = true;
    setProjectManagerLoading(true);
    setProjectManagerError(null);

    void Promise.all([
      ensurePlanningAgentFamilyDefinitions(),
      listAIAgentAssignments({
        nodeId: node.id,
      }),
      fetchBrainKBaseProcessOptions(),
    ])
      .then(([planningAgents, assignments, processes]) => {
        if (!active) return;
        const planningAgentOptions = planningAgents.filter(
          isSelectablePlanningAgentDefinition,
        );
        const compatiblePlanningAgentOptions = planningAgentOptions.filter(
          (agent) =>
            planningAgentSupportsNodeDomain(agent, effectivePlanningNodeDomain),
        );
        const filteredAssignments = assignments.filter(
          (entry) =>
            entry.nodeId === node.id &&
            isPlanningAgentAssignment(entry),
        );
        const assignment =
          filteredAssignments.find((entry) => entry.active) ??
          filteredAssignments[0] ??
          null;
        const fallbackAgentCode =
          cleanText(assignment?.agentCode) ||
          inferDefaultPlanningAgentCode(effectiveRoutingServiceCode, draftTeam);
        const nextDraft = assignment
          ? readProjectManagerAssignmentDraft(assignment)
          : {
              ...readProjectManagerAssignmentDraft(null),
              agentCode: fallbackAgentCode,
            };
        const nextVisibleAgentCodes = uniqueStrings([
          ...filteredAssignments.map((entry) => cleanText(entry.agentCode)),
          fallbackAgentCode,
        ]);
        const selectedAgent =
          compatiblePlanningAgentOptions.find(
            (agent) => cleanText(agent.code) === cleanText(assignment?.agentCode),
          ) ||
          compatiblePlanningAgentOptions.find(
            (agent) => cleanText(agent.code) === fallbackAgentCode,
          ) ||
          mapAssignmentToAgentDefinition(assignment) ||
          compatiblePlanningAgentOptions[0] ||
          null;

        setProjectManagerAgent(selectedAgent);
        setProjectManagerAgentOptions(planningAgentOptions);
        setProjectManagerAssignments(filteredAssignments);
        setProjectManagerAssignment(assignment);
        setProjectManagerProcesses(processes.filter((process) => process.status.toLowerCase() !== 'archived'));
        setProjectManagerDraft(nextDraft);
        setProjectManagerBaseline(nextDraft);
        setProjectManagerLoadedNodeId(node.id);
        setProjectManagerVisibleAgentCodes(nextVisibleAgentCodes);
        setProjectManagerPickerAgentCode(
          uniqueStrings(
            compatiblePlanningAgentOptions.map((agent) => cleanText(agent.code)).filter(
              (code) => !nextVisibleAgentCodes.includes(cleanText(code)),
            ),
          )[0] || fallbackAgentCode,
        );
        setProjectManagerOpenAgentCode(
          cleanText(assignment?.agentCode) || nextVisibleAgentCodes[0] || fallbackAgentCode || null,
        );
      })
      .catch((error) => {
        if (!active) return;
        setProjectManagerError(toErrorMessage(error));
      })
      .finally(() => {
        if (!active) return;
        setProjectManagerLoading(false);
      });

    return () => {
      active = false;
    };
  }, [draftTeam, effectivePlanningNodeDomain, effectiveRoutingServiceCode, node, open, projectManagerLoadedNodeId, tab]);

  const persistProjectManagerAssignment = async () => {
    if (!node || !canEdit || !selectedProjectManagerAgent) return;
    if (
      !planningAgentSupportsNodeDomain(
        selectedProjectManagerAgent,
        effectivePlanningNodeDomain,
      )
    ) {
      setProjectManagerError(
        'The selected planning agent does not match this node\'s planning domain.',
      );
      return;
    }
    if (projectManagerDraft.active && !projectManagerDraft.primaryProcessId.trim()) {
      setProjectManagerError('Attach a primary KBase process before enabling the planning agent.');
      return;
    }
    setProjectManagerSaving(true);
    setProjectManagerError(null);

    const targetAssignment =
      projectManagerAssignments.find(
        (entry) => cleanText(entry.agentCode) === cleanText(projectManagerDraft.agentCode),
      ) ?? null;
    const baseMetadata =
      targetAssignment?.metadata &&
      typeof targetAssignment.metadata === 'object' &&
      !Array.isArray(targetAssignment.metadata)
        ? targetAssignment.metadata
        : {};
    const primaryProcessId = projectManagerDraft.primaryProcessId.trim();
    const nextMetadata: Record<string, unknown> = {
      ...baseMetadata,
      primaryProcessId: primaryProcessId || null,
      attachedProcessIds: primaryProcessId ? [primaryProcessId] : [],
      autonomyMode: projectManagerDraft.autonomyMode,
      requireHumanApproval: projectManagerDraft.autonomyMode === 'human_in_loop',
      clientScopes: normalizeProjectManagerClientScopes(projectManagerDraft.clientScopes),
      clientOverrides: projectManagerDraft.clientOverrides.map((entry) => ({
        orgId: entry.orgId,
        autonomyMode: entry.autonomyMode,
        ...(Array.isArray(entry.scopes)
          ? { scopes: normalizeProjectManagerClientScopes(entry.scopes) }
          : {}),
      })),
      sourceServiceCode: effectiveRoutingServiceCode || null,
      sourceTierSkuId: effectiveRoutingTierSkuId || null,
    };
    delete nextMetadata.maxTicketsPerRun;
    delete nextMetadata.allowedProposalFields;

    try {
      const savedAssignment = targetAssignment
        ? await updateAIAgentAssignment(targetAssignment.id, {
            active: projectManagerDraft.active,
            priority: targetAssignment.priority,
            metadata: nextMetadata,
          })
        : await createAIAgentAssignment({
            agentId: selectedProjectManagerAgent.id,
            scopeType: 'brain_node',
            nodeId: node.id,
            active: projectManagerDraft.active,
            priority: 100,
            metadata: nextMetadata,
          });

      const otherAssignments = projectManagerAssignments.filter(
        (entry) => entry.id !== savedAssignment.id && entry.active,
      );
      if (otherAssignments.length) {
        await Promise.all(
          otherAssignments.map((entry) =>
            updateAIAgentAssignment(entry.id, {
              active: false,
            }),
          ),
        );
      }

      const nextDraft = readProjectManagerAssignmentDraft(savedAssignment);
      const nextAssignments = [
        ...projectManagerAssignments.filter((entry) => entry.id !== savedAssignment.id),
        savedAssignment,
        ...otherAssignments.map((entry) => ({
          ...entry,
          active: false,
        })),
      ].reduce<BrainAIAgentAssignment[]>((all, entry) => {
        if (all.some((candidate) => candidate.id === entry.id)) return all;
        all.push(entry);
        return all;
      }, []);
      setProjectManagerAssignment(savedAssignment);
      setProjectManagerAssignments(nextAssignments);
      setProjectManagerDraft(nextDraft);
      setProjectManagerBaseline(nextDraft);
      setProjectManagerVisibleAgentCodes((previous) =>
        uniqueStrings([...previous, cleanText(savedAssignment.agentCode)]),
      );
      setProjectManagerOpenAgentCode(cleanText(savedAssignment.agentCode) || null);
    } catch (error) {
      setProjectManagerError(toErrorMessage(error));
    } finally {
      setProjectManagerSaving(false);
    }
  };

  const confirmDeleteProjectManagerAssignment = async () => {
    if (!projectManagerDeleteTarget) return;
    setProjectManagerDeleting(true);
    setProjectManagerError(null);
    try {
      await deleteAIAgentAssignment(projectManagerDeleteTarget.assignmentId);
      const deletedAgentCode = cleanText(projectManagerDeleteTarget.agentCode);
      const nextAssignments = projectManagerAssignments.filter(
        (entry) => entry.id !== projectManagerDeleteTarget.assignmentId,
      );
      const nextVisibleAgentCodes = uniqueStrings(
        projectManagerVisibleAgentCodes.filter(
          (code) => cleanText(code) !== deletedAgentCode,
        ),
      );
      const nextAssignment =
        nextAssignments.find((entry) => entry.active) ??
        nextAssignments[0] ??
        null;
      const fallbackAgentCode =
        cleanText(nextAssignment?.agentCode) ||
        inferDefaultPlanningAgentCode(effectiveRoutingServiceCode, draftTeam);
      const nextAgentCode = cleanText(fallbackAgentCode);
      const nextDraft = nextAssignment
        ? readProjectManagerAssignmentDraft(nextAssignment)
        : {
            ...readProjectManagerAssignmentDraft(null),
            agentCode: nextAgentCode,
          };
      const nextAgent =
        projectManagerAgentOptions.find(
          (agent) => cleanText(agent.code) === nextAgentCode,
        ) ?? null;

      setProjectManagerAssignments(nextAssignments);
      setProjectManagerAssignment(nextAssignment);
      setProjectManagerAgent(nextAgent);
      setProjectManagerDraft(nextDraft);
      setProjectManagerBaseline(nextDraft);
      setProjectManagerVisibleAgentCodes(nextVisibleAgentCodes);
      setProjectManagerOpenAgentCode(
        nextAssignment
          ? cleanText(nextAssignment.agentCode)
          : nextVisibleAgentCodes[0] || null,
      );
      setProjectManagerPickerAgentCode(
        cleanText(
          availableProjectManagerAgentOptions.find(
            (agent) => cleanText(agent.code) !== deletedAgentCode,
          )?.code,
        ) || nextAgentCode,
      );
      setProjectManagerDeleteTarget(null);
    } catch (error) {
      setProjectManagerError(toErrorMessage(error));
    } finally {
      setProjectManagerDeleting(false);
    }
  };

  const selectProjectManagerProcess = (processId: string) => {
    setProjectManagerDraft((previous) => ({
      ...previous,
      primaryProcessId: processId,
    }));
    setProjectManagerProcessPickerOpen(false);
    setProjectManagerProcessSearch('');
  };

  const selectProjectManagerAgentCode = (agentCodeRaw: string) => {
    const nextAgentCode =
      cleanText(agentCodeRaw) ||
      inferDefaultPlanningAgentCode(effectiveRoutingServiceCode, draftTeam);
    const nextAgentDefinition =
      projectManagerAgentOptions.find(
        (agent) => cleanText(agent.code) === nextAgentCode,
      ) || null;
    if (
      nextAgentDefinition &&
      !planningAgentSupportsNodeDomain(
        nextAgentDefinition,
        effectivePlanningNodeDomain,
      )
    ) {
      setProjectManagerError(
        'That planning agent is not compatible with this node\'s planning domain.',
      );
      return;
    }
    setProjectManagerError(null);
    const existingAssignment =
      projectManagerAssignments.find(
        (entry) => cleanText(entry.agentCode) === nextAgentCode,
      ) ?? null;
    const nextDraft = existingAssignment
      ? readProjectManagerAssignmentDraft(existingAssignment)
      : {
          ...projectManagerDraft,
          agentCode: nextAgentCode,
          active: false,
          primaryProcessId: '',
        };
    const selectedAgent =
      nextAgentDefinition || mapAssignmentToAgentDefinition(existingAssignment);
    setProjectManagerAgent(selectedAgent ?? null);
    setProjectManagerAssignment(existingAssignment);
    setProjectManagerDraft(nextDraft);
    setProjectManagerVisibleAgentCodes((previous) => uniqueStrings([...previous, nextAgentCode]));
    setProjectManagerPickerAgentCode(nextAgentCode);
    setProjectManagerProcessPickerOpen(false);
    setProjectManagerProcessSearch('');
  };

  const setProjectManagerClientOverride = (
    orgId: string,
    autonomyMode: 'human_in_loop' | 'autonomous' | null,
  ) => {
    updateProjectManagerClientOverride(
      orgId,
      autonomyMode ? { autonomyMode } : null,
    );
  };

  const setProjectManagerGlobalScopes = (scopes: string[]) => {
    setProjectManagerDraft((previous) => {
      const nextScopes = normalizeProjectManagerClientScopes(scopes);
      return {
        ...previous,
        clientScopes: nextScopes,
        clientOverrides: previous.clientOverrides
          .map((entry) => {
            const shouldKeepScopes =
              Array.isArray(entry.scopes) &&
              !sameProjectManagerClientScopes(entry.scopes, nextScopes);
            const shouldKeepMode = entry.autonomyMode !== previous.autonomyMode;
            if (!shouldKeepScopes && !shouldKeepMode) return null;
            return {
              ...entry,
              scopes: shouldKeepScopes ? entry.scopes : null,
            };
          })
          .filter(
            (entry): entry is ProjectManagerClientOverride => Boolean(entry),
          ),
      };
    });
  };

  const addProjectManagerAgentCard = () => {
    const nextAgentCode = cleanText(projectManagerPickerAgentCode);
    if (!nextAgentCode) return;
    selectProjectManagerAgentCode(nextAgentCode);
    setProjectManagerOpenAgentCode(nextAgentCode);
  };

  const persistRouting = (serviceCode: string, tierSkuId: string) => {
    if (!node || !canEdit) return;

    const normalizedServiceCode = serviceCode.trim();
    const normalizedTierSkuId = tierSkuId.trim();
    const savedTeamLabel = toMetaText(node.meta?.team);
    const nextRules = {
      ...(node.rules && typeof node.rules === 'object' && !Array.isArray(node.rules)
        ? node.rules
        : {}),
    } as Record<string, unknown>;

    if (!normalizedServiceCode) {
      delete nextRules.routing;
      setDraftRoutingServiceCode('');
      setDraftRoutingTierSkuId('');
      setRoutingValidationMessage(null);
      onRulesChange(node.id, nextRules);
      return;
    }

    if (!savedTeamLabel.trim()) {
      setRoutingValidationMessage(
        draftTeam.trim()
          ? 'Blur the Team label field to save it before assigning service routing.'
          : 'Save a Team label on this node before assigning service routing.',
      );
      return;
    }

    nextRules.routing = {
      serviceCode: normalizedServiceCode,
      tierSkuId: normalizedTierSkuId || null,
    };
    setDraftRoutingServiceCode(normalizedServiceCode);
    setDraftRoutingTierSkuId(normalizedTierSkuId);
    setRoutingValidationMessage(null);
    onRulesChange(node.id, nextRules);
  };

  const persistTeamSettings = (nextSettings: TeamSettingsDraft) => {
    if (!node || !canEdit) return;
    const nextRules = {
      ...(node.rules && typeof node.rules === 'object' && !Array.isArray(node.rules)
        ? node.rules
        : {}),
      teamSettings: {
        roundRobinEnabled: Boolean(nextSettings.roundRobinEnabled),
        teamCapacityHours: Math.max(1, Math.min(200, Math.round(nextSettings.teamCapacityHours))),
      },
    } as Record<string, unknown>;

    setTeamSettingsDraft({
      roundRobinEnabled: Boolean(nextSettings.roundRobinEnabled),
      teamCapacityHours: Math.max(1, Math.min(200, Math.round(nextSettings.teamCapacityHours))),
    });
    setTeamSettingsBaseline({
      roundRobinEnabled: Boolean(nextSettings.roundRobinEnabled),
      teamCapacityHours: Math.max(1, Math.min(200, Math.round(nextSettings.teamCapacityHours))),
    });
    onRulesChange(node.id, nextRules);
  };

  const handleAddRosterMember = async (uid: string) => {
    if (!node || !canEdit) return;
    const normalizedUid = cleanText(uid);
    if (!normalizedUid) return;
    setTeamRosterSavingUid(normalizedUid);
    setTeamRosterError(null);
    try {
      await addOrganizationTreeRosterMember(node.id, { uid: normalizedUid, role: 'member' });
      setTeamRosterPickerOpen(false);
      await loadTeamCapacitySummary(node.id);
    } catch (error) {
      setTeamRosterError(toErrorMessage(error));
    } finally {
      setTeamRosterSavingUid(null);
    }
  };

  const handleRemoveRosterMember = async (uid: string) => {
    if (!node || !canEdit) return;
    const normalizedUid = cleanText(uid);
    if (!normalizedUid) return;
    setTeamRosterSavingUid(normalizedUid);
    setTeamRosterError(null);
    try {
      await removeOrganizationTreeRosterMember(node.id, normalizedUid);
      await loadTeamCapacitySummary(node.id);
    } catch (error) {
      setTeamRosterError(toErrorMessage(error));
    } finally {
      setTeamRosterSavingUid(null);
    }
  };

  const handleCreateProjectManagerRun = async (orgIdOverride?: string | null) => {
    if (!node || !projectManagerAgent) return;
    const normalizedRunPath =
      projectManagerRunPath === 'existing_project' || projectManagerRunPath === 'new_project'
        ? projectManagerRunPath
        : null;
    if (!normalizedRunPath) return;
    const normalizedOrgId = cleanText(orgIdOverride) || cleanText(projectManagerRunOrgId);
    setProjectManagerRunOrgId(normalizedOrgId);
    setProjectManagerRunSaving(true);
    setProjectManagerRunsError(null);
    try {
      const createdRun = await createProjectManagerRun({
        nodeId: node.id,
        orgId: normalizedOrgId || null,
        projectId:
          normalizedRunPath === 'existing_project'
            ? cleanText(projectManagerRunProjectId) || null
            : null,
        instruction: cleanText(projectManagerRunInstruction) || undefined,
      });
      setProjectManagerRuns((previous) => {
        const remaining = previous.filter((run) => run.id !== createdRun.id);
        return [createdRun, ...remaining];
      });
      setProjectManagerRunInstruction('');
      await loadProjectManagerRuns(node.id);
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    } finally {
      setProjectManagerRunSaving(false);
    }
  };

  const handleReviewProjectManagerApproval = async (
    approvalId: string,
    status: 'approved' | 'rejected' | 'canceled',
    reviewNoteOverride?: string,
  ) => {
    if (!node) return;
    const normalizedApprovalId = cleanText(approvalId);
    if (!normalizedApprovalId) return;
    setProjectManagerApprovalActionId(normalizedApprovalId);
    setProjectManagerRunsError(null);
    const nextReviewNote =
      reviewNoteOverride !== undefined
        ? cleanText(reviewNoteOverride)
        : cleanText(projectManagerReviewNotes[normalizedApprovalId]);
    try {
      await reviewProjectManagerApproval(normalizedApprovalId, {
        status,
        reviewNote: nextReviewNote || undefined,
      });
      setProjectManagerReviewNotes((previous) => ({
        ...previous,
        [normalizedApprovalId]: '',
      }));
      await loadProjectManagerRuns(node.id);
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    } finally {
      setProjectManagerApprovalActionId(null);
    }
  };

  const handleAcceptAndApplyProjectManagerApproval = async (
    approvalId: string,
    reviewNoteOverride?: string,
  ) => {
    if (!node) return;
    const normalizedApprovalId = cleanText(approvalId);
    if (!normalizedApprovalId) return;
    setProjectManagerApprovalActionId(normalizedApprovalId);
    setProjectManagerRunsError(null);
    const nextReviewNote =
      reviewNoteOverride !== undefined
        ? cleanText(reviewNoteOverride)
        : cleanText(projectManagerReviewNotes[normalizedApprovalId]);
    try {
      await reviewProjectManagerApproval(normalizedApprovalId, {
        status: 'approved',
        reviewNote: nextReviewNote || undefined,
      });
      await applyProjectManagerApproval(normalizedApprovalId);
      setProjectManagerReviewNotes((previous) => ({
        ...previous,
        [normalizedApprovalId]: '',
      }));
      await Promise.all([
        loadProjectManagerRuns(node.id),
        loadProjectManagerNodeProjects(node.id),
      ]);
      setProjectManagerRunInstruction('');
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    } finally {
      setProjectManagerApprovalActionId(null);
    }
  };

  const renderProjectManagerConversationContent = (
    client: ProjectManagerClientAccount,
  ) => {
    const clientProjects = projectManagerNodeProjects.filter(
      (project) => project.orgId === client.id,
    );
    const clientRuns = projectManagerRuns
      .filter((run) => cleanText(run.orgId) === client.id)
      .sort(
        (left, right) =>
          new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
      );
    const latestClientRun = clientRuns.length ? clientRuns[clientRuns.length - 1] : null;
    const latestApprovalId = cleanText(latestClientRun?.approval?.id);
    const latestApprovalStatus = cleanText(latestClientRun?.approval?.status).toLowerCase();
    const latestGuidanceRequests = latestClientRun
      ? getProjectManagerGuidanceRequests(latestClientRun)
      : [];
    const latestActionBusy = latestApprovalId
      ? projectManagerApprovalActionId === latestApprovalId
      : false;
    const clientRunBusy =
      projectManagerRunSaving && cleanText(projectManagerRunOrgId) === client.id;
    const clientRunRunning = clientRuns.some(
      (run) => cleanText(run.status).toLowerCase() === 'running',
    );

    return (
      <ProposalConversation>
        <Field>
          Select the project build path
          <Select
            value={projectManagerRunPath}
            disabled={projectManagerRunSaving || projectManagerLoading}
            onChange={(event) =>
              setProjectManagerRunPath(
                event.target.value === 'existing_project'
                  ? 'existing_project'
                  : event.target.value === 'new_project'
                    ? 'new_project'
                    : '',
              )
            }
          >
            <option value="">Select path</option>
            <option value="existing_project">Existing project</option>
            <option value="new_project">Create new project</option>
          </Select>
        </Field>
        {!projectManagerRunPath ? (
          <RunFlowHint>
            Choose whether this is a new project build or an update to an existing project before
            starting the PM workflow.
          </RunFlowHint>
        ) : null}
        {projectManagerRunPath === 'existing_project' ? (
          <Field>
            Select an existing project
            <Select
              value={projectManagerRunProjectId}
              disabled={projectManagerRunSaving || projectManagerLoading}
              onChange={(event) => setProjectManagerRunProjectId(event.target.value)}
            >
              <option value="">Select a project</option>
              {clientProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : projectManagerRunPath === 'new_project' ? (
          <>
            <Field>
              Resource review mode
              <RunFlowSwitchRow>
                <RunFlowSwitch
                  type="button"
                  $active={projectManagerRunResourceMode === 'all_resources'}
                  disabled={projectManagerRunSaving || projectManagerLoading}
                  onClick={() => setProjectManagerRunResourceMode('all_resources')}
                >
                  Review all available resources
                </RunFlowSwitch>
                <RunFlowSwitch
                  type="button"
                  $active={projectManagerRunResourceMode === 'specific_resources'}
                  disabled={projectManagerRunSaving || projectManagerLoading}
                  onClick={() => setProjectManagerRunResourceMode('specific_resources')}
                >
                  Use specific resources
                </RunFlowSwitch>
              </RunFlowSwitchRow>
            </Field>
            <RunFlowHint>
              {projectManagerRunResourceMode === 'all_resources'
                ? 'The PM agent will review the available client context and decide whether to create a new project or extend existing work.'
                : 'Use the conversation below to point the PM agent toward the notes, prior projects, or constraints it should use for this run.'}
            </RunFlowHint>
          </>
        ) : null}

        <ProposalConversationTimeline>
          {!clientRuns.length ? (
            <ProposalConversationMessage $role="system">
              <ProposalConversationMeta $role="system">PM agent</ProposalConversationMeta>
              <ProposalConversationBubble $role="system">
                <ProposalConversationTitle>Ready to begin the chat</ProposalConversationTitle>
                <ProposalConversationText>
                  Start the chat below. The PM agent will build the project here, surface project
                  and milestone decisions as they happen, and stop for explicit approval when
                  human permission is required.
                </ProposalConversationText>
              </ProposalConversationBubble>
            </ProposalConversationMessage>
          ) : null}
          {clientRuns.map((run) => {
            const promptInstruction = getProjectManagerPromptInstruction(run);
            const proposal = getProjectManagerProposal(run);
            const project = asObject(proposal.project);
            const milestones = Array.isArray(proposal.milestones) ? proposal.milestones : [];
            const tickets = Array.isArray(proposal.tickets) ? proposal.tickets : [];
            const warnings = getProjectManagerWarnings(run);
            const decisionLog = getProjectManagerDecisionLog(run);
            const guidanceRequests = getProjectManagerGuidanceRequests(run);
            const runStatus = cleanText(run.status).toLowerCase();
            const approvalId = cleanText(run.approval?.id);
            const approvalStatus = cleanText(run.approval?.status).toLowerCase();
            const actionBusy = approvalId
              ? projectManagerApprovalActionId === approvalId
              : false;
            const projectAction = cleanText(project?.action) || 'reviewed';
            const targetProjectName =
              cleanText(project?.name) ||
              getProjectManagerScopeValue(run, 'projectId') ||
              'Client-wide planning';
            const toolUpdates = run.toolExecutions
              .slice()
              .sort(
                (left, right) =>
                  new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
              )
              .map((tool) => {
                const toolStatus = cleanText(tool.status).toLowerCase();
                const toolName = cleanText(tool.toolName) || 'tool';
                return `${toolName} / ${toolStatus || 'running'}`;
              });
            const shouldShowPermissionActions =
              run.id === latestClientRun?.id &&
              Boolean(approvalId) &&
              approvalStatus === 'pending';

            return (
              <div key={run.id} style={{ display: 'grid', gap: 10 }}>
                {promptInstruction ? (
                  <ProposalConversationMessage $role="user">
                    <ProposalConversationMeta $role="user">
                      You / {formatDateTimeLabel(run.createdAt)}
                    </ProposalConversationMeta>
                    <ProposalConversationBubble $role="user">
                      <ProposalConversationText>{promptInstruction}</ProposalConversationText>
                    </ProposalConversationBubble>
                  </ProposalConversationMessage>
                ) : null}

                <ProposalConversationMessage $role="assistant">
                  <ProposalConversationMeta $role="assistant">
                    PM agent / {formatDateTimeLabel(run.createdAt)}
                  </ProposalConversationMeta>
                  <ProposalConversationBubble $role="assistant">
                    <ProposalConversationTitle>
                      {run.summary ||
                        (runStatus === 'running'
                          ? 'Building project'
                          : 'Project Manager update')}
                    </ProposalConversationTitle>
                    {run.errorText ? (
                      <ProposalConversationText>{run.errorText}</ProposalConversationText>
                    ) : (
                      <>
                        <ProposalConversationText>
                          {run.summary || `Project ${projectAction} on ${targetProjectName}.`}
                        </ProposalConversationText>
                        <ProposalConversationList>
                          <ProposalConversationItem>
                            Project action: {projectAction}
                          </ProposalConversationItem>
                          <ProposalConversationItem>
                            Target: {targetProjectName}
                          </ProposalConversationItem>
                          <ProposalConversationItem>
                            Milestones planned: {milestones.length}
                          </ProposalConversationItem>
                          <ProposalConversationItem>
                            Tickets planned: {tickets.length}
                          </ProposalConversationItem>
                        </ProposalConversationList>
                      </>
                    )}

                    {decisionLog.length ? (
                      <ProposalConversationList>
                        {decisionLog.map((entry, index) => (
                          <ProposalConversationItem key={`${run.id}-decision-${index}`}>
                            {entry}
                          </ProposalConversationItem>
                        ))}
                      </ProposalConversationList>
                    ) : null}

                    {toolUpdates.length ? (
                      <ProposalConversationList>
                        {toolUpdates.map((entry, index) => (
                          <ProposalConversationItem key={`${run.id}-tool-${index}`}>
                            {entry}
                          </ProposalConversationItem>
                        ))}
                      </ProposalConversationList>
                    ) : null}

                    {warnings.length ? (
                      <ProposalConversationList>
                        {warnings.map((warning, index) => (
                          <ProposalConversationItem key={`${run.id}-warning-${index}`}>
                            Warning: {warning}
                          </ProposalConversationItem>
                        ))}
                      </ProposalConversationList>
                    ) : null}

                    {guidanceRequests.length ? (
                      <ProposalConversationList>
                        {guidanceRequests.map((request, index) => (
                          <ProposalConversationItem key={`${run.id}-guidance-${index}`}>
                            {request.title}: {request.question}
                            {request.reason ? ` / ${request.reason}` : ''}
                          </ProposalConversationItem>
                        ))}
                      </ProposalConversationList>
                    ) : null}

                    {shouldShowPermissionActions ? (
                      <ProposalConversationActions>
                        <AIAgentInlineButton
                          type="button"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleAcceptAndApplyProjectManagerApproval(
                              approvalId,
                              projectManagerRunInstruction,
                            );
                          }}
                        >
                          {actionBusy ? 'Applying...' : 'Accept'}
                        </AIAgentInlineButton>
                        <AIAgentInlineButton
                          type="button"
                          $tone="danger"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleReviewProjectManagerApproval(
                              approvalId,
                              'rejected',
                              projectManagerRunInstruction,
                            );
                          }}
                        >
                          {actionBusy ? 'Saving...' : 'Reject'}
                        </AIAgentInlineButton>
                      </ProposalConversationActions>
                    ) : null}
                  </ProposalConversationBubble>
                </ProposalConversationMessage>

                {run.approval?.reviewNote ? (
                  <ProposalConversationMessage $role="user">
                    <ProposalConversationMeta $role="user">
                      Reviewer note / {formatDateTimeLabel(run.approval.reviewedAt || run.updatedAt)}
                    </ProposalConversationMeta>
                    <ProposalConversationBubble $role="user">
                      <ProposalConversationText>{run.approval.reviewNote}</ProposalConversationText>
                    </ProposalConversationBubble>
                  </ProposalConversationMessage>
                ) : null}

                {run.approval?.appliedSummary ? (
                  <ProposalConversationMessage $role="system">
                    <ProposalConversationMeta $role="system">
                      Applied / {formatDateTimeLabel(run.updatedAt)}
                    </ProposalConversationMeta>
                    <ProposalConversationBubble $role="system">
                      <ProposalConversationTitle>
                        AI chat accepted and applied
                      </ProposalConversationTitle>
                      <ProposalConversationText>
                        The approved PM chat outcome has been written into this client workspace.
                      </ProposalConversationText>
                    </ProposalConversationBubble>
                  </ProposalConversationMessage>
                ) : null}
              </div>
            );
          })}

          {latestClientRun && cleanText(latestClientRun.status).toLowerCase() === 'running' ? (
            <ProposalConversationMessage $role="system">
              <ProposalConversationMeta $role="system">PM agent / live</ProposalConversationMeta>
              <ProposalConversationBubble $role="system">
                <ProposalConversationTitle>Building the project in real time</ProposalConversationTitle>
                <ProposalConversationText>
                  The PM agent is reviewing client context, project history, milestones, and
                  ticket structure right now.
                </ProposalConversationText>
              </ProposalConversationBubble>
            </ProposalConversationMessage>
          ) : null}
        </ProposalConversationTimeline>

        <ProposalConversationComposer>
          <ProposalConversationComposerRow>
            <Input
              value={projectManagerRunInstruction}
              placeholder={
                projectManagerRunPath === 'existing_project'
                  ? 'Add context for what the PM agent should review, update, or confirm on this project.'
                  : projectManagerRunResourceMode === 'all_resources'
                    ? 'Add optional context before beginning the chat.'
                    : 'Tell the PM agent which notes, prior projects, or constraints to prioritize.'
              }
              disabled={projectManagerLoading || projectManagerRunSaving || latestActionBusy}
              onChange={(event) => setProjectManagerRunInstruction(event.target.value)}
            />
            <AISaveButton
              type="button"
              disabled={
                !canRunProjectManager ||
                !projectManagerRunPath ||
                (projectManagerRunPath === 'existing_project' &&
                  !cleanText(projectManagerRunProjectId)) ||
                projectManagerRunSaving ||
                projectManagerLoading ||
                clientRunRunning
              }
              onClick={() => {
                void handleCreateProjectManagerRun(client.id);
              }}
            >
              {clientRunBusy
                ? 'Working...'
                : clientRuns.length
                  ? 'Send to PM agent'
                  : 'Begin chat'}
            </AISaveButton>
          </ProposalConversationComposerRow>
          <RunFlowHint>
              {!canRunProjectManager
                ? 'Enable the PM agent and attach a primary KBase process before beginning the chat.'
              : !projectManagerRunPath
                ? 'Select the project build path first so the PM agent knows whether it should create new work or update an existing project.'
              : latestApprovalStatus === 'pending' && latestGuidanceRequests.length
                ? 'The PM agent is waiting for permission. Use Accept or Reject, or send another message to provide more context before the next pass.'
                : clientRunRunning
                  ? 'The PM agent is actively building this project. New context can be sent after the current pass completes.'
                  : 'Use the input to collaborate with the PM agent. It will stop and surface Accept / Reject only when it needs your permission.'}
          </RunFlowHint>
        </ProposalConversationComposer>
      </ProposalConversation>
    );
  };

  const handleApplyProjectManagerApproval = async (approvalId: string) => {
    if (!node) return;
    const normalizedApprovalId = cleanText(approvalId);
    if (!normalizedApprovalId) return;
    setProjectManagerApprovalActionId(normalizedApprovalId);
    setProjectManagerRunsError(null);
    try {
      await applyProjectManagerApproval(normalizedApprovalId);
      await Promise.all([
        loadProjectManagerRuns(node.id),
        loadProjectManagerNodeProjects(node.id),
      ]);
      setProjectManagerViewTab('logs');
    } catch (error) {
      setProjectManagerRunsError(toErrorMessage(error));
    } finally {
      setProjectManagerApprovalActionId(null);
    }
  };

  void renderProjectManagerConversationContent;

  const addChildDesignationRule = () => {
    if (!canEdit || !node) return;
    const rule = singularizeDesignation(childDesignationInput.trim());
    if (!rule) return;
    const existing = parseChildDesignationRules(draftChildDesignation);
    const hasDuplicate = existing.some((item) => item.toLowerCase() === rule.toLowerCase());
    if (hasDuplicate) {
      setChildDesignationInput('');
      return;
    }
    const nextRules = [...existing, rule];
    const serialized = serializeChildDesignationRules(nextRules);
    setChildDesignationInput('');
    persistMeta({
      position: draftPosition,
      team: draftTeam,
      designation: draftDesignation,
      childDesignation: serialized,
      appointedAgent: draftAppointedAgent,
      appointedAgentUid: draftAppointedAgentUid,
      appointedAgentEmail: draftAppointedAgentEmail,
      appointedAgentAvatarUrl: draftAppointedAgentAvatarUrl,
    });
  };

  const removeChildDesignationRule = (rule: string) => {
    if (!canEdit || !node) return;
    const nextRules = parseChildDesignationRules(draftChildDesignation).filter(
      (item) => item.toLowerCase() !== rule.toLowerCase(),
    );
    const serialized = serializeChildDesignationRules(nextRules);
    persistMeta({
      position: draftPosition,
      team: draftTeam,
      designation: draftDesignation,
      childDesignation: serialized,
      appointedAgent: draftAppointedAgent,
      appointedAgentUid: draftAppointedAgentUid,
      appointedAgentEmail: draftAppointedAgentEmail,
      appointedAgentAvatarUrl: draftAppointedAgentAvatarUrl,
    });
  };

  const renderOverview = () => {
    if (!node) return null;

    return (
      <Panel>
        <Field>
          Node name
          <Input
            value={draftTitle}
            disabled={!canEdit}
            onChange={(event) => setDraftTitle(event.target.value)}
            onBlur={() => {
              if (!canEdit) return;
              onTitleChange(node.id, draftTitle.trim() || node.title);
            }}
          />
        </Field>
        <Field>
          Position title
          <Input
            value={draftPosition}
            disabled={!canEdit}
            placeholder="Role title"
            onChange={(event) => setDraftPosition(event.target.value)}
            onBlur={() =>
              persistMeta({
                position: draftPosition,
                team: draftTeam,
                designation: draftDesignation,
                childDesignation: draftChildDesignation,
                appointedAgent: draftAppointedAgent,
                appointedAgentUid: draftAppointedAgentUid,
                appointedAgentEmail: draftAppointedAgentEmail,
                appointedAgentAvatarUrl: draftAppointedAgentAvatarUrl,
              })
            }
          />
        </Field>
        <Field>
          Team label
          <Input
            value={draftTeam}
            disabled={!canEdit}
            placeholder="Required for routed service teams"
            onChange={(event) => setDraftTeam(event.target.value)}
            onBlur={() =>
              persistMeta({
                position: draftPosition,
                team: draftTeam,
                designation: draftDesignation,
                childDesignation: draftChildDesignation,
                appointedAgent: draftAppointedAgent,
                appointedAgentUid: draftAppointedAgentUid,
                appointedAgentEmail: draftAppointedAgentEmail,
                appointedAgentAvatarUrl: draftAppointedAgentAvatarUrl,
              })
            }
          />
          {localRoutingMissingTeamLabel ? (
            <RuleHint>Required while this node has local service routing.</RuleHint>
          ) : effectiveRoutingServiceCode && !localRoutingRule ? (
            <RuleHint>
              Inherited routing uses the routed source node&apos;s Team label, not this child node.
            </RuleHint>
          ) : (
            <RuleHint>Use the Team label as the canonical queue-team name for routed nodes.</RuleHint>
          )}
        </Field>
        <Field>
          Node designation
          <Input
            value={draftDesignation}
            disabled={!canEdit}
            placeholder="Example: Department, Division, Team"
            onChange={(event) => setDraftDesignation(event.target.value)}
            onBlur={() =>
              persistMeta({
                position: draftPosition,
                team: draftTeam,
                designation: draftDesignation,
                childDesignation: draftChildDesignation,
                appointedAgent: draftAppointedAgent,
                appointedAgentUid: draftAppointedAgentUid,
                appointedAgentEmail: draftAppointedAgentEmail,
                appointedAgentAvatarUrl: draftAppointedAgentAvatarUrl,
              })
            }
          />
        </Field>
        <Field>
          Child designation rules
          <RuleEditor>
            <RulePillRow>
              {childDesignationRules.length ? (
                childDesignationRules.map((rule) => (
                  <RulePill key={rule}>
                    {rule}
                    {canEdit ? (
                      <RulePillRemove
                        type="button"
                        aria-label={`Remove ${rule}`}
                        onClick={() => removeChildDesignationRule(rule)}
                      >
                        x
                      </RulePillRemove>
                    ) : null}
                  </RulePill>
                ))
              ) : (
                <RuleHint>No child designation rules defined yet.</RuleHint>
              )}
            </RulePillRow>
            <RuleInputRow>
              <Input
                value={childDesignationInput}
                disabled={!canEdit}
                placeholder="Add child designation (e.g. Department, Division, Team)"
                onChange={(event) => setChildDesignationInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addChildDesignationRule();
                  }
                }}
              />
              <RuleAddButton
                type="button"
                disabled={!canEdit || !childDesignationInput.trim()}
                onClick={addChildDesignationRule}
              >
                Add
              </RuleAddButton>
            </RuleInputRow>
          </RuleEditor>
        </Field>
        <Field>
          Agent appointed (admins/agents only)
          <AgentPickerShell ref={agentPickerRef}>
            <AgentPickerInputRow>
              <Input
                value={agentSearch}
                disabled={!canEdit}
                placeholder="Search admins and agents..."
                onFocus={() => {
                  if (!canEdit) return;
                  setAgentPickerOpen(true);
                }}
                onChange={(event) => {
                  if (!canEdit) return;
                  setAgentSearch(event.target.value);
                  setAgentPickerOpen(true);
                }}
              />
              {draftAppointedAgentUid || draftAppointedAgent ? (
                <AIAgentIconButton
                  type="button"
                  aria-label="Remove appointed agent"
                  title="Remove appointed agent"
                  disabled={!canEdit}
                  $tone="danger"
                  onClick={() => {
                    clearAppointedAgentAssignment();
                  }}
                >
                  <TrashCanIcon />
                </AIAgentIconButton>
              ) : null}
            </AgentPickerInputRow>
            {agentPickerOpen && canEdit ? (
              <AgentPickerMenu>
                {operatorsLoading ? (
                  <AgentPickerEmpty>Loading team members...</AgentPickerEmpty>
                ) : matchingOperators.length ? (
                  matchingOperators.map((operator) => (
                    <AgentOption
                      key={operator.id}
                      type="button"
                      onMouseDown={(event) => {
                        if (!canEdit) return;
                        event.preventDefault();
                        const selectedLabel = operator.name || operator.email;
                        const selectedAvatarUrl = operator.avatarUrl?.trim() || '';
                        setDraftAppointedAgent(selectedLabel);
                        setDraftAppointedAgentUid(operator.id);
                        setDraftAppointedAgentEmail(operator.email);
                        setDraftAppointedAgentAvatarUrl(selectedAvatarUrl);
                        setAgentSearch(selectedLabel);
                        setAgentPickerOpen(false);
                        persistMeta({
                          position: draftPosition,
                          team: draftTeam,
                          designation: draftDesignation,
                          childDesignation: draftChildDesignation,
                          appointedAgent: selectedLabel,
                          appointedAgentUid: operator.id,
                          appointedAgentEmail: operator.email,
                          appointedAgentAvatarUrl: selectedAvatarUrl,
                        });
                      }}
                    >
                      {operator.name}
                      <AgentOptionSub>
                        {operator.email} - {operator.role.toUpperCase()}
                      </AgentOptionSub>
                    </AgentOption>
                  ))
                ) : (
                  <AgentPickerEmpty>No matching admin/agent users.</AgentPickerEmpty>
                )}
              </AgentPickerMenu>
            ) : null}
          </AgentPickerShell>
        </Field>
      </Panel>
    );
  };

  const renderRouting = () => {
    if (!node) return null;

    const tierSelectionDisabled =
      !effectiveRoutingServiceCode.trim() ||
      !selectedRoutingService ||
      selectedRoutingService.tiers.length === 0;

    return (
      <Panel>
        <RoutingHint>
          Routing determines which node acts as the ticket queue team for a purchased service.
          Child nodes inherit visibility through this routed node.
        </RoutingHint>
        <Field>
          Service route
          <GlassPicklist<string>
            value={effectiveRoutingServiceCode}
            options={serviceRoutingPicklistOptions}
            onChange={(nextServiceCode) => {
              const fallbackTier =
                nextServiceCode === effectiveRoutingServiceCode
                  ? effectiveRoutingTierSkuId
                  : '';
              persistRouting(nextServiceCode, fallbackTier);
            }}
            ariaLabel="Service routing selector"
            placeholder="-- No service routing --"
            searchable
            searchPlaceholder="Search service..."
            emptyLabel="No services available."
            disabled={!canEdit || serviceRoutingOptionsLoading}
          />
        </Field>
        <Field>
          Tier route (optional)
          <GlassPicklist<string>
            value={effectiveRoutingTierSkuId}
            options={tierRoutingPicklistOptions}
            onChange={(nextTierSkuId) => {
              persistRouting(effectiveRoutingServiceCode, nextTierSkuId);
            }}
            ariaLabel="Tier routing selector"
            placeholder="-- Service-level routing (all tiers) --"
            searchable
            searchPlaceholder="Search tier..."
            emptyLabel="No tiers available for this service."
            disabled={!canEdit || serviceRoutingOptionsLoading || tierSelectionDisabled}
          />
        </Field>
        {inheritedRoutingRule && !localRoutingRule ? (
          <RuleHint>
            Inherited from {inheritedRoutingRule.sourceNodeTitle}. Set a value here to override at this node.
          </RuleHint>
        ) : null}
        {routingValidationMessage ? (
          <RuleHint>{routingValidationMessage}</RuleHint>
        ) : null}
        {localRoutingMissingTeamLabel ? (
          <RuleHint>Save a Team label in Overview before keeping service routing on this node.</RuleHint>
        ) : null}
        {!effectiveRoutingServiceCode ? (
          <RuleHint>No routing is currently configured for this node.</RuleHint>
        ) : null}
      </Panel>
    );
  };

  const renderAIAgentLegacy = () => {
    if (!node) return null;

    const hasRoutedService = Boolean(effectiveRoutingServiceCode);

    return (
      <Panel>
        <AIPanelHeader>
          <AIPanelTitleWrap>
            <AIPanelTitle>Project Manager Agent</AIPanelTitle>
            <AIPanelSub>
              Configure the global PM defaults for this node here, then use the client tab for
              per-account autonomy and scope controls.
            </AIPanelSub>
          </AIPanelTitleWrap>
          <ToggleButton
            type="button"
            disabled={!canEdit || projectManagerLoading || projectManagerSaving}
            $active={projectManagerDraft.active}
            onClick={() =>
              setProjectManagerDraft((previous) => ({
                ...previous,
                active: !previous.active,
              }))
            }
          >
            {projectManagerDraft.active ? 'Enabled' : 'Disabled'}
          </ToggleButton>
        </AIPanelHeader>

        {projectManagerError ? (
          <AIPanelNotice $tone="error">{projectManagerError}</AIPanelNotice>
        ) : null}

        {projectManagerLoading ? (
          <AIPanelNotice>Loading node-scoped AI configuration...</AIPanelNotice>
        ) : (
          <>
            <AIStatusRow>
              <StatusChip $tone={projectManagerDraft.active ? 'success' : 'default'}>
                {projectManagerDraft.active ? 'Node assignment active' : 'Node assignment inactive'}
              </StatusChip>
              <StatusChip $tone="warning">Proposal only</StatusChip>
              <StatusChip>Human review required</StatusChip>
            </AIStatusRow>

            <AIReadonlyBlock>
              <AIReadonlyRow>
                <AIReadonlyLabel>Effective routed service</AIReadonlyLabel>
                <AIReadonlyValue>
                  {selectedRoutingService
                    ? `${selectedRoutingService.serviceTitle}${
                        selectedRoutingTier ? ` / ${selectedRoutingTier.name}` : ''
                      }`
                    : 'No service routing on this node'}
                </AIReadonlyValue>
              </AIReadonlyRow>
              <AIReadonlyRow>
                <AIReadonlyLabel>Assignment scope</AIReadonlyLabel>
                <AIReadonlyValue>{node.title}</AIReadonlyValue>
              </AIReadonlyRow>
            </AIReadonlyBlock>

            <PermissionGroup>
              <PermissionGroupHeader>
                <PermissionGroupTitle>Primary KBase process</PermissionGroupTitle>
                <PermissionGroupHint>
                  Attach the planning process this node-level PM agent should follow.
                </PermissionGroupHint>
              </PermissionGroupHeader>
              <ProcessFinder ref={projectManagerProcessPickerRef}>
                <ProcessTrigger
                  type="button"
                  $open={projectManagerProcessPickerOpen}
                  aria-label="Search KBase processes"
                  disabled={!canEdit || projectManagerLoading || projectManagerSaving}
                  onClick={() => setProjectManagerProcessPickerOpen((current) => !current)}
                >
                  <ProcessTriggerValue $placeholder={!selectedProjectManagerProcess}>
                    {selectedProjectManagerProcess?.title || 'Search KBase processes'}
                  </ProcessTriggerValue>
                </ProcessTrigger>
                {projectManagerProcessPickerOpen ? (
                  <ProcessPopover role="dialog" aria-label="KBase process search">
                    <ProcessSearchInput
                      value={projectManagerProcessSearch}
                      onChange={(event) => setProjectManagerProcessSearch(event.target.value)}
                      placeholder="Search KBase processes"
                      aria-label="Search KBase processes"
                      autoFocus
                    />
                    <ProcessResults>
                      {!cleanText(projectManagerProcessSearch) ? (
                        <ProcessResultSection>
                          <ProcessResultHeading>Suggested processes</ProcessResultHeading>
                          <ProcessResultButton
                            type="button"
                            $active={!projectManagerDraft.primaryProcessId}
                            onClick={() => selectProjectManagerProcess('')}
                          >
                            <ProcessResultTop>
                              <ProcessResultTitle>No attached process</ProcessResultTitle>
                              <ProcessResultBadge>Clear</ProcessResultBadge>
                            </ProcessResultTop>
                            <ProcessResultMeta>
                              Leave this node without a bound KBase planning process.
                            </ProcessResultMeta>
                          </ProcessResultButton>
                          {suggestedProjectManagerProcesses.length > 0 ? (
                            suggestedProjectManagerProcesses.map((process) => {
                              const metaParts = [
                                process.serviceLabel || process.serviceCode || '',
                                process.category || '',
                                process.subcategories[0] || '',
                              ].filter(Boolean);
                              return (
                                <ProcessResultButton
                                  key={process.id}
                                  type="button"
                                  $active={projectManagerDraft.primaryProcessId === process.id}
                                  onClick={() => selectProjectManagerProcess(process.id)}
                                >
                                  <ProcessResultTop>
                                    <ProcessResultTitle>{process.title}</ProcessResultTitle>
                                    <ProcessResultBadge>{process.status}</ProcessResultBadge>
                                  </ProcessResultTop>
                                  <ProcessResultMeta>
                                    {metaParts.length
                                      ? metaParts.join(' / ')
                                      : process.processType === 'internal'
                                        ? `Internal process / ${process.category || 'Project Management'}`
                                        : 'No scope metadata'}
                                  </ProcessResultMeta>
                                </ProcessResultButton>
                              );
                            })
                          ) : (
                            <ProcessEmptyState>
                              No KBase processes available yet.
                            </ProcessEmptyState>
                          )}
                        </ProcessResultSection>
                      ) : searchedProjectManagerProcesses.length > 0 ? (
                        <ProcessResultSection>
                          <ProcessResultHeading>Search results</ProcessResultHeading>
                          {searchedProjectManagerProcesses.map((process) => {
                            const metaParts = [
                              process.serviceLabel || process.serviceCode || '',
                              process.category || '',
                              process.subcategories[0] || '',
                            ].filter(Boolean);
                            return (
                              <ProcessResultButton
                                key={process.id}
                                type="button"
                                $active={projectManagerDraft.primaryProcessId === process.id}
                                onClick={() => selectProjectManagerProcess(process.id)}
                              >
                                <ProcessResultTop>
                                  <ProcessResultTitle>{process.title}</ProcessResultTitle>
                                  <ProcessResultBadge>{process.status}</ProcessResultBadge>
                                </ProcessResultTop>
                                <ProcessResultMeta>
                                  {metaParts.length
                                    ? metaParts.join(' / ')
                                    : process.processType === 'internal'
                                      ? `Internal process / ${process.category || 'Project Management'}`
                                      : 'No scope metadata'}
                                </ProcessResultMeta>
                              </ProcessResultButton>
                            );
                          })}
                        </ProcessResultSection>
                      ) : (
                        <ProcessEmptyState>
                          No KBase processes match that search.
                        </ProcessEmptyState>
                      )}
                    </ProcessResults>
                  </ProcessPopover>
                ) : null}
              </ProcessFinder>
              <RuleHint>
                {hasRoutedService
                  ? 'Processes matching this node’s routed service are surfaced first.'
                  : 'This node has no routed service yet, so all KBase processes are available.'}
              </RuleHint>
            </PermissionGroup>

            <TeamSettingRow>
              <div style={{ display: 'grid', gap: 3 }}>
                <TeamSettingTitle>Global PM scopes</TeamSettingTitle>
                <TeamSettingMeta>
                  {`${projectManagerGlobalScopeValues.length} scope${
                    projectManagerGlobalScopeValues.length === 1 ? '' : 's'
                  } enabled by default for every organization on this node. Organization overrides can narrow or change that behavior.`}
                </TeamSettingMeta>
              </div>
              <TeamSettingControl>
                <ClientScopeButton
                  type="button"
                  disabled={!canEdit || projectManagerLoading || projectManagerSaving}
                  onClick={() => setProjectManagerGlobalScopeDrawerOpen(true)}
                >
                  Open scopes
                </ClientScopeButton>
              </TeamSettingControl>
            </TeamSettingRow>

            {selectedProjectManagerProcess ? (
              <AIPanelNotice>
                {selectedProjectManagerProcess.title}
                {selectedProjectManagerProcess.serviceLabel
                  ? ` · ${selectedProjectManagerProcess.serviceLabel}`
                  : ''}
                {selectedProjectManagerProcess.category
                  ? ` · ${selectedProjectManagerProcess.category}`
                  : ''}
              </AIPanelNotice>
            ) : null}

            <AISaveRow>
              <AISaveHint>
                {projectManagerAssignment
                  ? 'This node keeps its own AI assignment separate from routing.'
                  : 'No AI assignment saved on this node yet.'}
              </AISaveHint>
              <AISaveButton
                type="button"
                disabled={!canEdit || !projectManagerDirty || projectManagerSaving || projectManagerLoading}
                onClick={() => {
                  void persistProjectManagerAssignment();
                }}
              >
                {projectManagerSaving ? 'Saving...' : 'Save AI agent'}
              </AISaveButton>
            </AISaveRow>
          </>
        )}
      </Panel>
    );
  };

  void renderAIAgentLegacy;

  const renderAIAgent = () => {
    if (!node) return null;

    const hasRoutedService = Boolean(effectiveRoutingServiceCode);
    const canRunProjectManager =
      projectManagerDraft.active &&
      Boolean(projectManagerDraft.primaryProcessId.trim());
    const selectedPlanningAgentTitle =
      cleanText(selectedProjectManagerAgent?.title) || 'Planning Agent';
    const selectedPlanningAgentDescription =
      cleanText(selectedProjectManagerAgent?.roleSummary) ||
      'Configure the planning agent and attached process for this node.';

    const renderConfig = () => (
      <AIAgentSection>
        <AIStatusRow>
          <StatusChip>{`${projectManagerDraft.clientOverrides.length} organization override${
            projectManagerDraft.clientOverrides.length === 1 ? '' : 's'
          }`}</StatusChip>
        </AIStatusRow>

        <RuleHint>{selectedPlanningAgentDescription}</RuleHint>

        <PermissionGroup>
          <PermissionGroupHeader>
            <PermissionGroupTitle>Primary KBase process</PermissionGroupTitle>
            <PermissionGroupHint>
              Attach the planning process this node-level planning agent should follow.
            </PermissionGroupHint>
          </PermissionGroupHeader>
          <ProcessFinder ref={projectManagerProcessPickerRef}>
            <ProcessTrigger
              type="button"
              $open={projectManagerProcessPickerOpen}
              aria-label="Search KBase processes"
              disabled={!canEdit || projectManagerLoading || projectManagerSaving}
              onClick={() => setProjectManagerProcessPickerOpen((current) => !current)}
            >
              <ProcessTriggerValue $placeholder={!selectedProjectManagerProcess}>
                {selectedProjectManagerProcess?.title || 'Search KBase processes'}
              </ProcessTriggerValue>
            </ProcessTrigger>
            {projectManagerProcessPickerOpen ? (
              <ProcessPopover role="dialog" aria-label="KBase process search">
                <ProcessSearchInput
                  value={projectManagerProcessSearch}
                  onChange={(event) => setProjectManagerProcessSearch(event.target.value)}
                  placeholder="Search KBase processes"
                  aria-label="Search KBase processes"
                  autoFocus
                />
                <ProcessResults>
                  {!cleanText(projectManagerProcessSearch) ? (
                    <ProcessResultSection>
                      <ProcessResultHeading>Suggested processes</ProcessResultHeading>
                      <ProcessResultButton
                        type="button"
                        $active={!projectManagerDraft.primaryProcessId}
                        onClick={() => selectProjectManagerProcess('')}
                      >
                        <ProcessResultTop>
                          <ProcessResultTitle>No attached process</ProcessResultTitle>
                          <ProcessResultBadge>Clear</ProcessResultBadge>
                        </ProcessResultTop>
                        <ProcessResultMeta>
                          Leave this node without a bound KBase planning process.
                        </ProcessResultMeta>
                      </ProcessResultButton>
                      {suggestedProjectManagerProcesses.length > 0 ? (
                        suggestedProjectManagerProcesses.map((process) => {
                          const metaParts = [
                            process.serviceLabel || process.serviceCode || '',
                            process.category || '',
                            process.subcategories[0] || '',
                          ].filter(Boolean);
                          return (
                            <ProcessResultButton
                              key={process.id}
                              type="button"
                              $active={projectManagerDraft.primaryProcessId === process.id}
                              onClick={() => selectProjectManagerProcess(process.id)}
                            >
                              <ProcessResultTop>
                                <ProcessResultTitle>{process.title}</ProcessResultTitle>
                                <ProcessResultBadge>{process.status}</ProcessResultBadge>
                              </ProcessResultTop>
                              <ProcessResultMeta>
                                {metaParts.length
                                  ? metaParts.join(' / ')
                                  : process.processType === 'internal'
                                    ? `Internal process / ${process.category || 'Project Management'}`
                                    : 'No scope metadata'}
                              </ProcessResultMeta>
                            </ProcessResultButton>
                          );
                        })
                      ) : (
                        <ProcessEmptyState>No KBase processes available yet.</ProcessEmptyState>
                      )}
                    </ProcessResultSection>
                  ) : searchedProjectManagerProcesses.length > 0 ? (
                    <ProcessResultSection>
                      <ProcessResultHeading>Search results</ProcessResultHeading>
                      {searchedProjectManagerProcesses.map((process) => {
                        const metaParts = [
                          process.serviceLabel || process.serviceCode || '',
                          process.category || '',
                          process.subcategories[0] || '',
                        ].filter(Boolean);
                        return (
                          <ProcessResultButton
                            key={process.id}
                            type="button"
                            $active={projectManagerDraft.primaryProcessId === process.id}
                            onClick={() => selectProjectManagerProcess(process.id)}
                          >
                            <ProcessResultTop>
                              <ProcessResultTitle>{process.title}</ProcessResultTitle>
                              <ProcessResultBadge>{process.status}</ProcessResultBadge>
                            </ProcessResultTop>
                            <ProcessResultMeta>
                              {metaParts.length
                                ? metaParts.join(' / ')
                                : process.processType === 'internal'
                                  ? `Internal process / ${process.category || 'Project Management'}`
                                  : 'No scope metadata'}
                            </ProcessResultMeta>
                          </ProcessResultButton>
                        );
                      })}
                    </ProcessResultSection>
                  ) : (
                    <ProcessEmptyState>No KBase processes match that search.</ProcessEmptyState>
                  )}
                </ProcessResults>
              </ProcessPopover>
            ) : null}
          </ProcessFinder>
          <RuleHint>
            {hasRoutedService
              ? 'Processes matching this node’s routed service are surfaced first.'
              : 'This node has no routed service yet, so all KBase processes are available.'}
          </RuleHint>
        </PermissionGroup>

        <TeamSettingRow>
          <div style={{ display: 'grid', gap: 3 }}>
            <TeamSettingTitle>Global PM scopes</TeamSettingTitle>
            <TeamSettingMeta>
              {`${projectManagerGlobalScopeValues.length} scope${
                projectManagerGlobalScopeValues.length === 1 ? '' : 's'
              } enabled by default for every organization on this node. Organization overrides can narrow or change that behavior.`}
            </TeamSettingMeta>
          </div>
          <TeamSettingControl>
            <ClientScopeButton
              type="button"
              disabled={!canEdit || projectManagerLoading || projectManagerSaving}
              onClick={() => setProjectManagerGlobalScopeDrawerOpen(true)}
            >
              Open scopes
            </ClientScopeButton>
          </TeamSettingControl>
        </TeamSettingRow>

        {selectedProjectManagerProcess ? (
          <AIPanelNotice>
            {selectedProjectManagerProcess.title}
            {selectedProjectManagerProcess.serviceLabel
              ? ` / ${selectedProjectManagerProcess.serviceLabel}`
              : ''}
            {selectedProjectManagerProcess.category
              ? ` / ${selectedProjectManagerProcess.category}`
              : ''}
          </AIPanelNotice>
        ) : null}

        <AISaveRow>
          <AISaveHint>
            {projectManagerAssignment
              ? 'This node sets the planning-agent defaults. Organization pages can override execution mode and scopes per account when needed.'
              : 'No planning-agent assignment saved on this node yet.'}
          </AISaveHint>
          <AIAgentRunActions>
            {canEdit && projectManagerDirty ? (
              <AISaveButton
                type="button"
                disabled={projectManagerSaving || projectManagerLoading}
                onClick={() => {
                  void persistProjectManagerAssignment();
                }}
              >
                {projectManagerSaving ? 'Saving...' : 'Save AI agent'}
              </AISaveButton>
            ) : null}
          </AIAgentRunActions>
        </AISaveRow>
      </AIAgentSection>
    );

    const renderReview = () => {
      if (projectManagerRunsLoading) {
        return <AIPanelNotice>Loading planning proposals...</AIPanelNotice>;
      }
      if (!projectManagerPendingRuns.length) {
        return <Placeholder>No pending planning proposals exist for this node yet.</Placeholder>;
      }
      return (
        <AIAgentList>
          {projectManagerPendingRuns.map((run) => {
            const proposal = getProjectManagerProposal(run);
            const project = asObject(proposal.project);
            const milestones = Array.isArray(proposal.milestones) ? proposal.milestones : [];
            const tickets = Array.isArray(proposal.tickets) ? proposal.tickets : [];
            const warnings = getProjectManagerWarnings(run);
            const guidanceRequests = getProjectManagerGuidanceRequests(run);
            const approvalStatus = cleanText(run.approval?.status).toLowerCase();
            const approvalId = cleanText(run.approval?.id);
            const actionBusy = projectManagerApprovalActionId === approvalId;

            return (
              <AIAgentRunCard key={run.id}>
                <AIAgentRunHeader>
                  <div style={{ display: 'grid', gap: 4 }}>
                    <AIAgentRunTitle>{run.summary || `${selectedPlanningAgentTitle} proposal`}</AIAgentRunTitle>
                    <AIAgentRunMeta>
                      {formatDateTimeLabel(run.createdAt)}
                      {run.orgName ? ` / ${run.orgName}` : ''}
                      {run.requestedBy?.name ? ` / ${run.requestedBy.name}` : ''}
                      {getProjectManagerScopeValue(run, 'processTitle')
                        ? ` / ${getProjectManagerScopeValue(run, 'processTitle')}`
                        : ''}
                    </AIAgentRunMeta>
                  </div>
                  <AIAgentRunActions>
                    <StatusChip $tone={approvalStatus === 'approved' ? 'success' : 'warning'}>
                      {approvalStatus || 'pending'}
                    </StatusChip>
                    {approvalId && approvalStatus === 'pending' ? (
                      <>
                        <AIAgentInlineButton
                          type="button"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleReviewProjectManagerApproval(approvalId, 'approved');
                          }}
                        >
                          {actionBusy ? 'Saving...' : 'Approve'}
                        </AIAgentInlineButton>
                        <AIAgentInlineButton
                          type="button"
                          $tone="danger"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleReviewProjectManagerApproval(approvalId, 'rejected');
                          }}
                        >
                          {actionBusy ? 'Saving...' : 'Disapprove'}
                        </AIAgentInlineButton>
                      </>
                    ) : null}
                    {approvalId && approvalStatus === 'approved' ? (
                      <>
                        <AIAgentInlineButton
                          type="button"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleApplyProjectManagerApproval(approvalId);
                          }}
                        >
                          {actionBusy ? 'Applying...' : 'Apply changes'}
                        </AIAgentInlineButton>
                        <AIAgentInlineButton
                          type="button"
                          $tone="danger"
                          disabled={actionBusy}
                          onClick={() => {
                            void handleReviewProjectManagerApproval(approvalId, 'canceled');
                          }}
                        >
                          {actionBusy ? 'Saving...' : 'Cancel'}
                        </AIAgentInlineButton>
                      </>
                    ) : null}
                  </AIAgentRunActions>
                </AIAgentRunHeader>

                <AIAgentKeyValueGrid>
                  <AIAgentKeyValue>
                    <AIAgentKey>Project action</AIAgentKey>
                    <AIAgentValue>{cleanText(project?.action) || 'none'}</AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Target project</AIAgentKey>
                    <AIAgentValue>
                      {cleanText(project?.name) ||
                        getProjectManagerScopeValue(run, 'projectId') ||
                        'New or unresolved'}
                    </AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Milestones</AIAgentKey>
                    <AIAgentValue>{milestones.length}</AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Tickets</AIAgentKey>
                    <AIAgentValue>{tickets.length}</AIAgentValue>
                  </AIAgentKeyValue>
                </AIAgentKeyValueGrid>

                {guidanceRequests.length ? (
                  <AIAgentSection>
                    <AIAgentKey>Guidance requests</AIAgentKey>
                    <AIAgentBulletList>
                      {guidanceRequests.map((request, index) => (
                        <AIAgentBullet key={`${run.id}-guidance-${index}`}>
                          <strong>{request.title}:</strong> {request.question}
                          {request.reason ? ` / ${request.reason}` : ''}
                        </AIAgentBullet>
                      ))}
                    </AIAgentBulletList>
                  </AIAgentSection>
                ) : null}

                {warnings.length ? (
                  <AIAgentSection>
                    <AIAgentKey>Warnings</AIAgentKey>
                    <AIAgentBulletList>
                      {warnings.map((warning, index) => (
                        <AIAgentBullet key={`${run.id}-warning-${index}`}>{warning}</AIAgentBullet>
                      ))}
                    </AIAgentBulletList>
                  </AIAgentSection>
                ) : null}

                {approvalId ? (
                  <Field>
                    Reviewer direction
                    <Textarea
                      value={projectManagerReviewNotes[approvalId] ?? ''}
                      placeholder="Add guidance or constraints for the next PM pass or explain your review decision."
                      disabled={actionBusy}
                      onChange={(event) =>
                        setProjectManagerReviewNotes((previous) => ({
                          ...previous,
                          [approvalId]: event.target.value,
                        }))
                      }
                    />
                  </Field>
                ) : null}

                {run.approval?.reviewNote ? (
                  <AIPanelNotice>{run.approval.reviewNote}</AIPanelNotice>
                ) : null}
              </AIAgentRunCard>
            );
          })}
        </AIAgentList>
      );
    };

    const renderProjectManagerConversationContent = (
      client: ProjectManagerClientAccount,
    ) => {
      const clientProjects = projectManagerNodeProjects.filter(
        (project) => project.orgId === client.id,
      );
      const clientRuns = projectManagerRuns
        .filter((run) => cleanText(run.orgId) === client.id)
        .sort(
          (left, right) =>
            new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
        );
      const latestClientRun = clientRuns.length ? clientRuns[clientRuns.length - 1] : null;
      const latestApprovalId = cleanText(latestClientRun?.approval?.id);
      const latestApprovalStatus = cleanText(latestClientRun?.approval?.status).toLowerCase();
      const latestGuidanceRequests = latestClientRun
        ? getProjectManagerGuidanceRequests(latestClientRun)
        : [];
      const latestActionBusy = latestApprovalId
        ? projectManagerApprovalActionId === latestApprovalId
        : false;
      const clientRunBusy =
        projectManagerRunSaving && cleanText(projectManagerRunOrgId) === client.id;
      const clientRunRunning = clientRuns.some(
        (run) => cleanText(run.status).toLowerCase() === 'running',
      );

      return (
        <ProposalConversation>
        <Field>
          Select the project build path
          <Select
            value={projectManagerRunPath}
            disabled={projectManagerRunSaving || projectManagerLoading}
            onChange={(event) =>
              setProjectManagerRunPath(
                event.target.value === 'existing_project'
                  ? 'existing_project'
                  : event.target.value === 'new_project'
                    ? 'new_project'
                    : '',
              )
            }
          >
            <option value="">Select path</option>
            <option value="existing_project">Existing project</option>
            <option value="new_project">Create new project</option>
          </Select>
        </Field>
        {!projectManagerRunPath ? (
          <RunFlowHint>
            Choose whether this is a new project build or an existing project build before
            starting the PM workflow.
          </RunFlowHint>
        ) : null}
        {projectManagerRunPath === 'existing_project' ? (
            <Field>
              Select an existing project
              <Select
                value={projectManagerRunProjectId}
                disabled={projectManagerRunSaving || projectManagerLoading}
                onChange={(event) => setProjectManagerRunProjectId(event.target.value)}
              >
                <option value="">Select a project</option>
                {clientProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </Field>
        ) : projectManagerRunPath === 'new_project' ? (
          <>
              <Field>
                Resource review mode
                <RunFlowSwitchRow>
                  <RunFlowSwitch
                    type="button"
                    $active={projectManagerRunResourceMode === 'all_resources'}
                    disabled={projectManagerRunSaving || projectManagerLoading}
                    onClick={() => setProjectManagerRunResourceMode('all_resources')}
                  >
                    Review all available resources
                  </RunFlowSwitch>
                  <RunFlowSwitch
                    type="button"
                    $active={projectManagerRunResourceMode === 'specific_resources'}
                    disabled={projectManagerRunSaving || projectManagerLoading}
                    onClick={() => setProjectManagerRunResourceMode('specific_resources')}
                  >
                    Use specific resources
                  </RunFlowSwitch>
                </RunFlowSwitchRow>
              </Field>
              <RunFlowHint>
                {projectManagerRunResourceMode === 'all_resources'
                  ? 'The PM agent will review the available client context and decide whether to create a new project or extend existing work.'
                  : 'Use the conversation below to point the PM agent toward the notes, prior projects, or constraints it should use for this run.'}
              </RunFlowHint>
          </>
        ) : null}

          <ProposalConversationTimeline>
            {!clientRuns.length ? (
              <ProposalConversationMessage $role="system">
                <ProposalConversationMeta $role="system">
                  PM agent
                </ProposalConversationMeta>
                <ProposalConversationBubble $role="system">
                  <ProposalConversationTitle>Ready to begin the chat</ProposalConversationTitle>
                  <ProposalConversationText>
                    Start the conversation below. The PM agent will build the project
                    here, surface project and milestone decisions as they happen, and stop for
                    explicit approval when human permission is required.
                  </ProposalConversationText>
                </ProposalConversationBubble>
              </ProposalConversationMessage>
            ) : null}
            {clientRuns.map((run) => {
              const promptInstruction = getProjectManagerPromptInstruction(run);
              const proposal = getProjectManagerProposal(run);
              const project = asObject(proposal.project);
              const milestones = Array.isArray(proposal.milestones) ? proposal.milestones : [];
              const tickets = Array.isArray(proposal.tickets) ? proposal.tickets : [];
              const warnings = getProjectManagerWarnings(run);
              const decisionLog = getProjectManagerDecisionLog(run);
              const guidanceRequests = getProjectManagerGuidanceRequests(run);
              const runStatus = cleanText(run.status).toLowerCase();
              const approvalId = cleanText(run.approval?.id);
              const approvalStatus = cleanText(run.approval?.status).toLowerCase();
              const actionBusy = approvalId
                ? projectManagerApprovalActionId === approvalId
                : false;
              const projectAction = cleanText(project?.action) || 'reviewed';
              const targetProjectName =
                cleanText(project?.name) ||
                getProjectManagerScopeValue(run, 'projectId') ||
                'Client-wide planning';
              const toolUpdates = run.toolExecutions
                .slice()
                .sort(
                  (left, right) =>
                    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
                )
                .map((tool) => {
                  const toolStatus = cleanText(tool.status).toLowerCase();
                  const toolName = cleanText(tool.toolName) || 'tool';
                  return `${toolName} / ${toolStatus || 'running'}`;
                });
              const shouldShowPermissionActions =
                run.id === latestClientRun?.id &&
                Boolean(approvalId) &&
                approvalStatus === 'pending';

              return (
                <div key={run.id} style={{ display: 'grid', gap: 10 }}>
                  {promptInstruction ? (
                    <ProposalConversationMessage $role="user">
                      <ProposalConversationMeta $role="user">
                        You / {formatDateTimeLabel(run.createdAt)}
                      </ProposalConversationMeta>
                      <ProposalConversationBubble $role="user">
                        <ProposalConversationText>{promptInstruction}</ProposalConversationText>
                      </ProposalConversationBubble>
                    </ProposalConversationMessage>
                  ) : null}

                  <ProposalConversationMessage $role="assistant">
                    <ProposalConversationMeta $role="assistant">
                      PM agent / {formatDateTimeLabel(run.createdAt)}
                    </ProposalConversationMeta>
                    <ProposalConversationBubble $role="assistant">
                      <ProposalConversationTitle>
                      {run.summary ||
                        (runStatus === 'running'
                            ? 'Building project'
                            : 'Project Manager update')}
                      </ProposalConversationTitle>
                      {run.errorText ? (
                        <ProposalConversationText>{run.errorText}</ProposalConversationText>
                      ) : (
                        <>
                          <ProposalConversationText>
                            {run.summary ||
                              `Project ${projectAction} on ${targetProjectName}.`}
                          </ProposalConversationText>
                          <ProposalConversationList>
                            <ProposalConversationItem>
                              Project action: {projectAction}
                            </ProposalConversationItem>
                            <ProposalConversationItem>
                              Target: {targetProjectName}
                            </ProposalConversationItem>
                            <ProposalConversationItem>
                              Milestones planned: {milestones.length}
                            </ProposalConversationItem>
                            <ProposalConversationItem>
                              Tickets planned: {tickets.length}
                            </ProposalConversationItem>
                          </ProposalConversationList>
                        </>
                      )}

                      {decisionLog.length ? (
                        <ProposalConversationList>
                          {decisionLog.map((entry, index) => (
                            <ProposalConversationItem key={`${run.id}-decision-${index}`}>
                              {entry}
                            </ProposalConversationItem>
                          ))}
                        </ProposalConversationList>
                      ) : null}

                      {toolUpdates.length ? (
                        <ProposalConversationList>
                          {toolUpdates.map((entry, index) => (
                            <ProposalConversationItem key={`${run.id}-tool-${index}`}>
                              {entry}
                            </ProposalConversationItem>
                          ))}
                        </ProposalConversationList>
                      ) : null}

                      {warnings.length ? (
                        <ProposalConversationList>
                          {warnings.map((warning, index) => (
                            <ProposalConversationItem key={`${run.id}-warning-${index}`}>
                              Warning: {warning}
                            </ProposalConversationItem>
                          ))}
                        </ProposalConversationList>
                      ) : null}

                      {guidanceRequests.length ? (
                        <ProposalConversationList>
                          {guidanceRequests.map((request, index) => (
                            <ProposalConversationItem key={`${run.id}-guidance-${index}`}>
                              {request.title}: {request.question}
                              {request.reason ? ` / ${request.reason}` : ''}
                            </ProposalConversationItem>
                          ))}
                        </ProposalConversationList>
                      ) : null}

                      {shouldShowPermissionActions ? (
                        <ProposalConversationActions>
                          <AIAgentInlineButton
                            type="button"
                            disabled={actionBusy}
                            onClick={() => {
                              void handleAcceptAndApplyProjectManagerApproval(
                                approvalId,
                                projectManagerRunInstruction,
                              );
                            }}
                          >
                            {actionBusy ? 'Applying...' : 'Accept'}
                          </AIAgentInlineButton>
                          <AIAgentInlineButton
                            type="button"
                            $tone="danger"
                            disabled={actionBusy}
                            onClick={() => {
                              void handleReviewProjectManagerApproval(
                                approvalId,
                                'rejected',
                                projectManagerRunInstruction,
                              );
                            }}
                          >
                            {actionBusy ? 'Saving...' : 'Reject'}
                          </AIAgentInlineButton>
                        </ProposalConversationActions>
                      ) : null}
                    </ProposalConversationBubble>
                  </ProposalConversationMessage>

                  {run.approval?.reviewNote ? (
                    <ProposalConversationMessage $role="user">
                      <ProposalConversationMeta $role="user">
                        Reviewer note /{' '}
                        {formatDateTimeLabel(run.approval.reviewedAt || run.updatedAt)}
                      </ProposalConversationMeta>
                      <ProposalConversationBubble $role="user">
                        <ProposalConversationText>
                          {run.approval.reviewNote}
                        </ProposalConversationText>
                      </ProposalConversationBubble>
                    </ProposalConversationMessage>
                  ) : null}

                  {run.approval?.appliedSummary ? (
                    <ProposalConversationMessage $role="system">
                      <ProposalConversationMeta $role="system">
                        Applied / {formatDateTimeLabel(run.updatedAt)}
                      </ProposalConversationMeta>
                      <ProposalConversationBubble $role="system">
                        <ProposalConversationTitle>
                          AI chat accepted and applied
                        </ProposalConversationTitle>
                        <ProposalConversationText>
                          The approved PM proposal has been written into this client workspace.
                        </ProposalConversationText>
                      </ProposalConversationBubble>
                    </ProposalConversationMessage>
                  ) : null}
                </div>
              );
            })}

            {latestClientRun && cleanText(latestClientRun.status).toLowerCase() === 'running' ? (
              <ProposalConversationMessage $role="system">
                <ProposalConversationMeta $role="system">
                  PM agent / live
                </ProposalConversationMeta>
                <ProposalConversationBubble $role="system">
                  <ProposalConversationTitle>
                    Building the project in real time
                  </ProposalConversationTitle>
                  <ProposalConversationText>
                    The PM agent is reviewing client context, project history, milestones, and
                    ticket structure right now.
                  </ProposalConversationText>
                </ProposalConversationBubble>
              </ProposalConversationMessage>
            ) : null}
          </ProposalConversationTimeline>

          <ProposalConversationComposer>
            <ProposalConversationComposerRow>
              <Input
                value={projectManagerRunInstruction}
                placeholder={
                  projectManagerRunPath === 'existing_project'
                    ? 'Add context for what the PM agent should review, update, or confirm on this project.'
                    : projectManagerRunResourceMode === 'all_resources'
                      ? 'Add optional context before beginning the chat.'
                      : 'Tell the PM agent which notes, prior projects, or constraints to prioritize.'
                }
                disabled={projectManagerLoading || projectManagerRunSaving || latestActionBusy}
                onChange={(event) => setProjectManagerRunInstruction(event.target.value)}
              />
              <AISaveButton
                type="button"
                disabled={
                  !canRunProjectManager ||
                  !projectManagerRunPath ||
                  (projectManagerRunPath === 'existing_project' &&
                    !cleanText(projectManagerRunProjectId)) ||
                  projectManagerRunSaving ||
                  projectManagerLoading ||
                  clientRunRunning
                }
                onClick={() => {
                  void handleCreateProjectManagerRun(client.id);
                }}
              >
                {clientRunBusy
                  ? 'Working...'
                  : clientRuns.length
                    ? 'Send to PM agent'
                    : 'Begin chat'}
              </AISaveButton>
            </ProposalConversationComposerRow>
            <RunFlowHint>
              {!canRunProjectManager
                ? 'Enable the PM agent and attach a primary KBase process before beginning the chat.'
                : !projectManagerRunPath
                  ? 'Select the project build path first so the PM agent knows whether it should create new work or update an existing project.'
                : latestApprovalStatus === 'pending' && latestGuidanceRequests.length
                  ? 'The PM agent is waiting for permission. Use Accept or Reject, or send another message to provide more context before the next pass.'
                  : clientRunRunning
                    ? 'The PM agent is actively building this project. New context can be sent after the current pass completes.'
                    : 'Use the input to collaborate with the PM agent. It will stop and surface Accept / Reject only when it needs your permission.'}
            </RunFlowHint>
          </ProposalConversationComposer>
        </ProposalConversation>
      );
    };

    void renderProjectManagerConversationContent;

    const renderClient = () => {
      if (projectManagerClientsLoading) {
        return <AIPanelNotice>Loading client controls...</AIPanelNotice>;
      }
      if (projectManagerClientsError) {
        return <AIPanelNotice $tone="error">{projectManagerClientsError}</AIPanelNotice>;
      }
      if (!projectManagerClientOptions.length) {
        return <Placeholder>No client accounts are available for PM override controls yet.</Placeholder>;
      }
      return (
        <div style={{ minHeight: '100%' }}>
          <AIAgentSection>
            <Field>
              Client search
              <Input
                value={projectManagerClientSearch}
                placeholder="Search client accounts..."
                onChange={(event) => setProjectManagerClientSearch(event.target.value)}
              />
            </Field>

            <AIAgentList>
              {filteredProjectManagerClients.map((client) => {
                const effectiveMode = getProjectManagerClientEffectiveMode(client.id);
                const isExpanded = projectManagerExpandedClientId === client.id;
                const clientScopes = getProjectManagerClientScopes(client.id);
                const clientProjects = projectManagerNodeProjects.filter(
                  (project) => project.orgId === client.id,
                );
                const clientRuns = projectManagerRuns.filter(
                  (run) => cleanText(run.orgId) === client.id,
                );
                const latestClientRun = clientRuns.length
                  ? clientRuns
                      .slice()
                      .sort(
                        (left, right) =>
                          new Date(left.createdAt).getTime() -
                          new Date(right.createdAt).getTime(),
                      )[clientRuns.length - 1] ?? null
                  : null;
                const clientRunRunning = clientRuns.some(
                  (run) => cleanText(run.status).toLowerCase() === 'running',
                );
                const canOpenProjectBuild =
                  Boolean(projectManagerRunPath) &&
                  (projectManagerRunPath !== 'existing_project' ||
                    Boolean(cleanText(projectManagerRunProjectId)));
                const serviceSummary = client.serviceLabels.length
                  ? client.serviceLabels.join(' / ')
                  : [client.assignedServiceTeam, ...client.assignedServiceTeams]
                      .filter(Boolean)
                      .join(' / ');
                const accountMeta = [
                  client.ownerEmail,
                  serviceSummary,
                  client.projectCountOnNode ? `${client.projectCountOnNode} project${client.projectCountOnNode === 1 ? '' : 's'}` : '',
                ]
                  .filter(Boolean)
                  .join(' / ');
                return (
                  <ClientAccordionCard key={client.id} $expanded={isExpanded}>
                    <ClientAccordionHeader
                      type="button"
                      $expanded={isExpanded}
                      onClick={() => {
                        setProjectManagerExpandedClientId((current) =>
                          current === client.id ? null : client.id,
                        );
                        setProjectManagerScopeDrawerOrgId(null);
                        setProjectManagerConversationDrawerOrgId(null);
                        setProjectManagerRunOrgId(client.id);
                        setProjectManagerRunProjectId('');
                        setProjectManagerRunInstruction('');
                        setProjectManagerRunPath('');
                        setProjectManagerRunResourceMode('all_resources');
                      }}
                    >
                      <ClientAccordionInfo>
                        <ClientAccordionTitle>{client.name}</ClientAccordionTitle>
                        <ClientAccordionMeta>
                          {accountMeta || 'No account metadata'}
                        </ClientAccordionMeta>
                      </ClientAccordionInfo>
                      <StatusChip $tone={effectiveMode === 'autonomous' ? 'success' : 'warning'}>
                        {effectiveMode === 'autonomous'
                          ? 'Fully autonomous'
                          : 'Human in the loop'}
                      </StatusChip>
                      <ClientAccordionExpand $expanded={isExpanded}>▾</ClientAccordionExpand>
                    </ClientAccordionHeader>
                    {isExpanded ? (
                      <ClientAccordionBody>
                        <ClientInlineRow>
                          <ClientInlineMeta>
                            <TeamSettingTitle>
                              {effectiveMode === 'autonomous'
                                ? 'Execution mode: Fully autonomous'
                                : 'Execution mode: Human in the loop'}
                            </TeamSettingTitle>
                          </ClientInlineMeta>
                          <TeamSettingControl>
                            <ModeStatusWrap>
                              <ModeStatusTitle>
                                {effectiveMode === 'autonomous'
                                  ? 'Fully autonomous'
                                  : 'Human in the loop'}
                              </ModeStatusTitle>
                              <ModeStatusMeta>
                                {effectiveMode === 'autonomous'
                                  ? 'The PM agent can apply approved client work automatically.'
                                  : 'The PM agent must stop for review before anything is applied.'}
                              </ModeStatusMeta>
                            </ModeStatusWrap>
                            <SwitchButton
                              type="button"
                              aria-pressed={effectiveMode === 'autonomous'}
                              disabled={!canEdit || projectManagerLoading || projectManagerSaving}
                              $active={effectiveMode === 'autonomous'}
                              onClick={(event) => {
                                event.stopPropagation();
                                setProjectManagerClientOverride(
                                  client.id,
                                  effectiveMode === 'autonomous'
                                    ? 'human_in_loop'
                                    : 'autonomous',
                                );
                              }}
                            >
                              <SwitchThumb $active={effectiveMode === 'autonomous'} />
                            </SwitchButton>
                          </TeamSettingControl>
                        </ClientInlineRow>

                        <ClientInlineRow>
                          <ClientInlineMeta>
                            <TeamSettingTitle>Client scopes</TeamSettingTitle>
                            <TeamSettingMeta>
                              {`${clientScopes.length} scope${clientScopes.length === 1 ? '' : 's'} enabled for this client.`}
                            </TeamSettingMeta>
                          </ClientInlineMeta>
                          <ClientScopeButton
                            type="button"
                            disabled={!canEdit}
                            onClick={(event) => {
                              event.stopPropagation();
                              setProjectManagerConversationDrawerOrgId(null);
                              setProjectManagerScopeDrawerOrgId(client.id);
                            }}
                          >
                            Open scopes
                          </ClientScopeButton>
                        </ClientInlineRow>

                        {effectiveMode === 'human_in_loop' ? (
                          <AIAgentSection>
                            <Field>
                              Select the project build path
                              <Select
                                value={projectManagerRunPath}
                                disabled={projectManagerRunSaving || projectManagerLoading}
                                onChange={(event) =>
                                  setProjectManagerRunPath(
                                    event.target.value === 'existing_project'
                                      ? 'existing_project'
                                      : event.target.value === 'new_project'
                                        ? 'new_project'
                                        : '',
                                  )
                                }
                              >
                                <option value="">Select path</option>
                                <option value="existing_project">Existing project</option>
                                <option value="new_project">Create new project</option>
                              </Select>
                            </Field>
                            {projectManagerRunPath === 'existing_project' ? (
                              <Field>
                                Select an existing project
                                <Select
                                  value={projectManagerRunProjectId}
                                  disabled={projectManagerRunSaving || projectManagerLoading}
                                  onChange={(event) =>
                                    setProjectManagerRunProjectId(event.target.value)
                                  }
                                >
                                  <option value="">Select a project</option>
                                  {clientProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                      {project.name}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                            ) : null}
                            <ClientInlineRow>
                              <ClientInlineMeta>
                                <TeamSettingTitle>
                                  <AIChatLabel>
                                    <AIChatIcon size={15} />
                                    <span>Chat</span>
                                  </AIChatLabel>
                                </TeamSettingTitle>
                                <TeamSettingMeta>
                                  {!canRunProjectManager
                                    ? 'Enable the PM agent and attach a primary KBase process in the config tab before starting a project build.'
                                    : !projectManagerRunPath
                                      ? 'Choose whether this is a new project or an existing project build first.'
                                    : projectManagerRunPath === 'existing_project' &&
                                        !cleanText(projectManagerRunProjectId)
                                      ? 'Select the target project before starting the build.'
                                    : clientRunRunning
                                      ? 'The PM agent is actively building a project for this client.'
                                    : latestClientRun
                                      ? `Latest PM update ${formatDateTimeLabel(latestClientRun.updatedAt)}.`
                                      : 'Open the chat drawer to begin and collaborate with the PM agent.'}
                                </TeamSettingMeta>
                              </ClientInlineMeta>
                              {canOpenProjectBuild ? (
                                <ClientScopeButton
                                  type="button"
                                  disabled={!canRunProjectManager}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setProjectManagerScopeDrawerOrgId(null);
                                    setProjectManagerConversationDrawerOrgId(client.id);
                                    setProjectManagerRunOrgId(client.id);
                                  }}
                                >
                                  <AIChatLabel>
                                    <AIChatIcon size={15} />
                                    <span>{clientRuns.length ? 'Open chat' : 'Begin chat'}</span>
                                  </AIChatLabel>
                                </ClientScopeButton>
                              ) : null}
                            </ClientInlineRow>
                          </AIAgentSection>
                        ) : null}
                      </ClientAccordionBody>
                    ) : null}
                  </ClientAccordionCard>
                );
              })}
            </AIAgentList>

            <AISaveRow>
              <AISaveHint>
                Client overrides and scopes stay local to this node and apply on top of the
                global PM configuration.
              </AISaveHint>
              <AIAgentRunActions>
                {canEdit && projectManagerDirty ? (
                  <AISaveButton
                    type="button"
                    disabled={projectManagerSaving || projectManagerLoading}
                    onClick={() => {
                      void persistProjectManagerAssignment();
                    }}
                  >
                    {projectManagerSaving ? 'Saving...' : 'Save AI agent'}
                  </AISaveButton>
                ) : null}
              </AIAgentRunActions>
            </AISaveRow>
          </AIAgentSection>

        </div>
      );
    };

    const renderLogs = () => {
      if (projectManagerRunsLoading) {
        return <AIPanelNotice>Loading planning-agent run logs...</AIPanelNotice>;
      }
      if (!projectManagerRuns.length) {
        return <Placeholder>No planning-agent runs have been recorded for this node yet.</Placeholder>;
      }
      return (
        <AIAgentSection>
          <Field>
            Client filter
            <Select
              value={projectManagerLogOrgFilter}
              onChange={(event) => setProjectManagerLogOrgFilter(event.target.value)}
            >
              <option value="">All clients</option>
              {projectManagerClientOptions.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </Field>
          <AIAgentList>
          {filteredProjectManagerRuns.map((run) => {
            const warnings = getProjectManagerWarnings(run);
            const decisionLog = getProjectManagerDecisionLog(run);
            const proposal = getProjectManagerProposal(run);
            const milestones = Array.isArray(proposal.milestones) ? proposal.milestones : [];
            const tickets = Array.isArray(proposal.tickets) ? proposal.tickets : [];
            return (
              <AIAgentRunCard key={run.id}>
                <AIAgentRunHeader>
                  <div style={{ display: 'grid', gap: 4 }}>
                    <AIAgentRunTitle>{run.summary || `${selectedPlanningAgentTitle} run`}</AIAgentRunTitle>
                    <AIAgentRunMeta>
                      Started {formatDateTimeLabel(run.startedAt || run.createdAt)}
                      {run.orgName ? ` / ${run.orgName}` : ''}
                      {run.completedAt ? ` / Completed ${formatDateTimeLabel(run.completedAt)}` : ''}
                    </AIAgentRunMeta>
                  </div>
                  <AIAgentRunActions>
                    <StatusChip>{cleanText(run.status) || 'unknown'}</StatusChip>
                    {run.approval?.status ? (
                      <StatusChip $tone={cleanText(run.approval.status).toLowerCase() === 'executed' ? 'success' : 'warning'}>
                        {run.approval.status}
                      </StatusChip>
                    ) : null}
                  </AIAgentRunActions>
                </AIAgentRunHeader>

                <AIAgentKeyValueGrid>
                  <AIAgentKeyValue>
                    <AIAgentKey>Client</AIAgentKey>
                    <AIAgentValue>{run.orgName || '--'}</AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Provider</AIAgentKey>
                    <AIAgentValue>
                      {[cleanText(run.provider), cleanText(run.model)].filter(Boolean).join(' / ') || '--'}
                    </AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Process</AIAgentKey>
                    <AIAgentValue>{getProjectManagerScopeValue(run, 'processTitle') || '--'}</AIAgentValue>
                  </AIAgentKeyValue>
                  <AIAgentKeyValue>
                    <AIAgentKey>Planned work</AIAgentKey>
                    <AIAgentValue>{`${milestones.length} milestone(s) / ${tickets.length} ticket(s)`}</AIAgentValue>
                  </AIAgentKeyValue>
                </AIAgentKeyValueGrid>

                {decisionLog.length ? (
                  <AIAgentSection>
                    <AIAgentKey>Reasoning</AIAgentKey>
                    <AIAgentBulletList>
                      {decisionLog.map((entry, index) => (
                        <AIAgentBullet key={`${run.id}-decision-${index}`}>{entry}</AIAgentBullet>
                      ))}
                    </AIAgentBulletList>
                  </AIAgentSection>
                ) : null}

                {warnings.length ? (
                  <AIAgentSection>
                    <AIAgentKey>Warnings</AIAgentKey>
                    <AIAgentBulletList>
                      {warnings.map((warning, index) => (
                        <AIAgentBullet key={`${run.id}-log-warning-${index}`}>{warning}</AIAgentBullet>
                      ))}
                    </AIAgentBulletList>
                  </AIAgentSection>
                ) : null}

                {run.approval?.reviewNote ? (
                  <AIAgentSection>
                    <AIAgentKey>Reviewer note</AIAgentKey>
                    <AIAgentValue>{run.approval.reviewNote}</AIAgentValue>
                  </AIAgentSection>
                ) : null}

                {run.toolExecutions.length ? (
                  <AIAgentSection>
                    <AIAgentKey>Tool trace</AIAgentKey>
                    <AIAgentBulletList>
                      {run.toolExecutions.map((tool) => (
                        <AIAgentBullet key={tool.id}>
                          {tool.toolName} / {tool.status} / {formatDateTimeLabel(tool.createdAt)}
                          {tool.errorText ? ` / ${tool.errorText}` : ''}
                        </AIAgentBullet>
                      ))}
                    </AIAgentBulletList>
                  </AIAgentSection>
                ) : null}

                {run.errorText ? <AIPanelNotice $tone="error">{run.errorText}</AIPanelNotice> : null}
              </AIAgentRunCard>
            );
          })}
          </AIAgentList>
        </AIAgentSection>
      );
    };

    const renderPlanningAgentAccordion = (agentCode: string) => {
      const accordionAgent =
        projectManagerAgentOptions.find(
          (agent) => cleanText(agent.code) === cleanText(agentCode),
        ) ?? null;
      const accordionAssignment =
        projectManagerAssignments.find(
          (entry) => cleanText(entry.agentCode) === cleanText(agentCode),
        ) ?? null;
      const accordionDraft =
        cleanText(projectManagerDraft.agentCode) === cleanText(agentCode)
          ? projectManagerDraft
          : readProjectManagerAssignmentDraft(accordionAssignment);
      const accordionExpanded =
        cleanText(projectManagerOpenAgentCode) === cleanText(agentCode);
      const accordionTitle =
        cleanText(accordionAgent?.title) ||
        cleanText(accordionAssignment?.agentTitle) ||
        'Planning Agent';
      const accordionDescription =
        cleanText(accordionAgent?.roleSummary) ||
        'Configure this node-level AI agent and the KBase process it should follow.';
      const accordionNeedsAttention =
        (accordionExpanded && Boolean(projectManagerError || projectManagerRunsError)) ||
        (accordionDraft.active && !accordionDraft.primaryProcessId.trim());

      return (
        <AIAgentAccordionCard key={agentCode} $expanded={accordionExpanded}>
          <AIAgentAccordionHeader $expanded={accordionExpanded}>
            <AIAgentAccordionToggle
              type="button"
              $expanded={accordionExpanded}
              onClick={() => {
                if (accordionExpanded) {
                  setProjectManagerOpenAgentCode(null);
                  return;
                }
                selectProjectManagerAgentCode(agentCode);
                setProjectManagerOpenAgentCode(agentCode);
              }}
              aria-expanded={accordionExpanded}
            >
            <AIAgentAccordionInfo>
              <AIPanelTitle>{accordionTitle}</AIPanelTitle>
              <AIPanelSub>{accordionDescription}</AIPanelSub>
              <AIAgentAccordionMeta>
                <StatusChip $tone={accordionDraft.active ? 'success' : 'default'}>
                  {accordionDraft.active ? 'Enabled' : 'Disabled'}
                </StatusChip>
                {projectManagerLoading && accordionExpanded ? <StatusChip>Loading</StatusChip> : null}
                {accordionNeedsAttention ? <StatusChip $tone="warning">Needs attention</StatusChip> : null}
              </AIAgentAccordionMeta>
            </AIAgentAccordionInfo>
            </AIAgentAccordionToggle>
            <AIAgentAccordionExpand $expanded={accordionExpanded}>▾</AIAgentAccordionExpand>
            {accordionAssignment ? (
              <AIAgentIconButton
                type="button"
                $tone="danger"
                aria-label={`Delete ${accordionTitle}`}
                disabled={!canEdit || projectManagerSaving || projectManagerDeleting}
                onClick={() =>
                  setProjectManagerDeleteTarget({
                    assignmentId: accordionAssignment.id,
                    agentCode: accordionAssignment.agentCode,
                    agentTitle: accordionTitle,
                  })
                }
              >
                <TrashCanIcon />
              </AIAgentIconButton>
            ) : null}
          </AIAgentAccordionHeader>

          {accordionExpanded ? (
            <AIAgentAccordionBody>
              <AIPanelHeader>
                <AIPanelTitleWrap>
                  <AIPanelTitle>Agent controls</AIPanelTitle>
                  <AIPanelSub>
                    Configure the node assignment and attached planning workflow for this AI agent.
                  </AIPanelSub>
                </AIPanelTitleWrap>
                <ToggleButton
                  type="button"
                  disabled={!canEdit || projectManagerLoading || projectManagerSaving}
                  $active={projectManagerDraft.active}
                  onClick={() =>
                    setProjectManagerDraft((previous) => ({
                      ...previous,
                      active: !previous.active,
                    }))
                  }
                >
                  {projectManagerDraft.active ? 'Enabled' : 'Disabled'}
                </ToggleButton>
              </AIPanelHeader>

              {projectManagerError ? <AIPanelNotice $tone="error">{projectManagerError}</AIPanelNotice> : null}
              {projectManagerRunsError ? <AIPanelNotice $tone="error">{projectManagerRunsError}</AIPanelNotice> : null}

              {projectManagerLoading ? (
                <AIPanelNotice>Loading node-scoped AI configuration...</AIPanelNotice>
              ) : (
                <AIAgentSection>
                  <AIAgentSubtabRow>
                    <AIAgentSubtab
                      type="button"
                      $active={projectManagerViewTab === 'config'}
                      onClick={() => setProjectManagerViewTab('config')}
                    >
                      Config
                    </AIAgentSubtab>
                    <AIAgentSubtab
                      type="button"
                      $active={projectManagerViewTab === 'logs'}
                      onClick={() => setProjectManagerViewTab('logs')}
                    >
                      Logs
                    </AIAgentSubtab>
                  </AIAgentSubtabRow>

                  {projectManagerViewTab === 'config' ? renderConfig() : null}
                  {projectManagerViewTab === 'logs' ? renderLogs() : null}
                </AIAgentSection>
              )}
            </AIAgentAccordionBody>
          ) : null}
        </AIAgentAccordionCard>
      );
    };

    void renderReview;
    void renderClient;

    return (
      <Panel>
        <AIAgentSection>
          <Field>
            Load AI agent
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <Select
                value={projectManagerPickerAgentCode}
                disabled={!canEdit || projectManagerLoading || !availableProjectManagerAgentOptions.length}
                onChange={(event) => setProjectManagerPickerAgentCode(event.target.value)}
              >
                {availableProjectManagerAgentOptions.length ? (
                  availableProjectManagerAgentOptions.map((agent) => (
                    <option key={agent.id} value={agent.code}>
                      {agent.title}
                    </option>
                  ))
                ) : (
                  <option value={projectManagerPickerAgentCode || ''}>
                    {compatibleProjectManagerAgentOptions.length
                      ? 'All compatible planning agents are already loaded'
                      : 'No compatible planning agents are available for this node'}
                  </option>
                )}
              </Select>
              <AISaveButton
                type="button"
                disabled={!canEdit || projectManagerLoading || !availableProjectManagerAgentOptions.length}
                onClick={addProjectManagerAgentCard}
              >
                Add agent
              </AISaveButton>
            </div>
          </Field>
          <RuleHint>
            Use the selector above to load an AI agent for this node. Each AI agent gets its own accordion card below.
          </RuleHint>
        </AIAgentSection>

        {renderedProjectManagerAgentCodes.length ? (
          renderedProjectManagerAgentCodes.map((agentCode) =>
            renderPlanningAgentAccordion(agentCode),
          )
        ) : (
          <Placeholder>No AI agents have been loaded for this node yet.</Placeholder>
        )}
        {false ? (
      <AIAgentAccordionCard $expanded={projectManagerAccordionOpen}>
          <AIAgentAccordionHeader
            $expanded={projectManagerAccordionOpen}
            onClick={() => setProjectManagerAccordionOpen((previous) => !previous)}
            aria-expanded={projectManagerAccordionOpen}
          >
            <AIAgentAccordionInfo>
              <AIPanelTitle>{selectedPlanningAgentTitle}</AIPanelTitle>
              <AIPanelSub>
                Attach the node-specific planning process, then set the global execution defaults
                that organizations inherit unless their account overrides them.
              </AIPanelSub>
              <AIAgentAccordionMeta>
                <StatusChip $tone={projectManagerDraft.active ? 'success' : 'default'}>
                  {projectManagerDraft.active ? 'Enabled' : 'Disabled'}
                </StatusChip>
                {projectManagerLoading ? (
                  <StatusChip>Loading</StatusChip>
                ) : null}
                {projectManagerError || projectManagerRunsError ? (
                  <StatusChip $tone="warning">Needs attention</StatusChip>
                ) : null}
              </AIAgentAccordionMeta>
            </AIAgentAccordionInfo>
            <AIAgentAccordionExpand $expanded={projectManagerAccordionOpen}>▾</AIAgentAccordionExpand>
          </AIAgentAccordionHeader>

          {projectManagerAccordionOpen ? (
            <AIAgentAccordionBody>
              <AIPanelHeader>
                <AIPanelTitleWrap>
                  <AIPanelTitle>Agent controls</AIPanelTitle>
                  <AIPanelSub>
                    Configure the node assignment and attached planning workflow for this planning agent.
                  </AIPanelSub>
                </AIPanelTitleWrap>
                <ToggleButton
                  type="button"
                  disabled={!canEdit || projectManagerLoading || projectManagerSaving}
                  $active={projectManagerDraft.active}
                  onClick={() =>
                    setProjectManagerDraft((previous) => ({
                      ...previous,
                      active: !previous.active,
                    }))
                  }
                >
                  {projectManagerDraft.active ? 'Enabled' : 'Disabled'}
                </ToggleButton>
              </AIPanelHeader>

              {projectManagerError ? <AIPanelNotice $tone="error">{projectManagerError}</AIPanelNotice> : null}
              {projectManagerRunsError ? <AIPanelNotice $tone="error">{projectManagerRunsError}</AIPanelNotice> : null}

              {projectManagerLoading ? (
                <AIPanelNotice>Loading node-scoped AI configuration...</AIPanelNotice>
              ) : (
                <AIAgentSection>
                  <AIAgentSubtabRow>
                    <AIAgentSubtab
                      type="button"
                      $active={projectManagerViewTab === 'config'}
                      onClick={() => setProjectManagerViewTab('config')}
                    >
                      Config
                    </AIAgentSubtab>
                    <AIAgentSubtab
                      type="button"
                      $active={projectManagerViewTab === 'logs'}
                      onClick={() => setProjectManagerViewTab('logs')}
                    >
                      Logs
                    </AIAgentSubtab>
                  </AIAgentSubtabRow>

                  {projectManagerViewTab === 'config' ? renderConfig() : null}
                  {projectManagerViewTab === 'logs' ? renderLogs() : null}
                </AIAgentSection>
              )}
            </AIAgentAccordionBody>
          ) : null}
        </AIAgentAccordionCard>
        ) : null}
      </Panel>
    );
  };

  const renderTeam = () => {
    const assignedMinutes = teamCapacitySummary?.assignedMinutes ?? 0;
    const unassignedMinutes = teamCapacitySummary?.unassignedMinutes ?? 0;
    const trackedTicketCount = teamCapacitySummary?.trackedTicketCount ?? 0;

    return (
      <Panel>
        <AIPanelHeader>
          <AIPanelTitleWrap>
            <AIPanelTitle>Team snapshot</AIPanelTitle>
            <AIPanelSub>
              Set how this team should accept routed work, then review each member’s current queue against the team capacity limit.
            </AIPanelSub>
          </AIPanelTitleWrap>
          {canEdit && teamSettingsDirty ? (
            <AISaveButton
              type="button"
              onClick={() => persistTeamSettings(teamSettingsDraft)}
            >
              Save team settings
            </AISaveButton>
          ) : null}
        </AIPanelHeader>

        <TeamSettingsGrid>
          <TeamCard>
            <TeamSettingRow>
              <div style={{ display: 'grid', gap: 3 }}>
                <TeamSettingTitle>Round-robin routing</TeamSettingTitle>
                <TeamSettingMeta>
                  {teamSettingsDraft.roundRobinEnabled
                    ? 'New work can route to the lowest-load eligible team member.'
                    : 'New work stays manual until someone assigns it.'}
                </TeamSettingMeta>
              </div>
              <SwitchButton
                type="button"
                aria-pressed={teamSettingsDraft.roundRobinEnabled}
                disabled={!canEdit}
                $active={teamSettingsDraft.roundRobinEnabled}
                onClick={() =>
                  setTeamSettingsDraft((previous) => ({
                    ...previous,
                    roundRobinEnabled: !previous.roundRobinEnabled,
                  }))
                }
              >
                <SwitchThumb $active={teamSettingsDraft.roundRobinEnabled} />
              </SwitchButton>
            </TeamSettingRow>
          </TeamCard>

          <TeamCard>
            <TeamSettingTitle>Team capacity limit</TeamSettingTitle>
            <div style={{ display: 'grid', gap: 6 }}>
              <Input
                type="number"
                min={1}
                max={200}
                value={String(teamSettingsDraft.teamCapacityHours)}
                disabled={!canEdit}
                onChange={(event) =>
                  setTeamSettingsDraft((previous) => ({
                    ...previous,
                    teamCapacityHours: Math.max(1, Math.min(200, Math.round(Number(event.target.value) || DEFAULT_TEAM_CAPACITY_HOURS))),
                  }))
                }
              />
              <TeamSettingMeta>
                A team member is treated as full once their remaining estimated queue reaches this many hours. Default is 30h.
              </TeamSettingMeta>
            </div>
          </TeamCard>
        </TeamSettingsGrid>

        <TeamFlatSection>
          <AIReadonlyLabel>Queue summary</AIReadonlyLabel>
          <CompactMetricStrip>
            <CompactMetricChip>
              <CompactMetricValue>
                {formatHoursLabel(assignedMinutes / 60)}
              </CompactMetricValue>
              <CompactMetricLabel>Assigned load</CompactMetricLabel>
            </CompactMetricChip>
            <CompactMetricChip>
              <CompactMetricValue>{formatHoursLabel(unassignedMinutes / 60)}</CompactMetricValue>
              <CompactMetricLabel>Unassigned queue</CompactMetricLabel>
            </CompactMetricChip>
            <CompactMetricChip>
              <CompactMetricValue>{trackedTicketCount}</CompactMetricValue>
              <CompactMetricLabel>Tracked tickets</CompactMetricLabel>
            </CompactMetricChip>
          </CompactMetricStrip>
        </TeamFlatSection>

        <TeamFlatSection>
          <TeamSectionHeader>
            <AIReadonlyLabel>Team members</AIReadonlyLabel>
            {canEdit ? (
              <AgentPickerShell ref={teamRosterPickerRef}>
                <TeamInlineAction
                  type="button"
                  disabled={Boolean(teamRosterSavingUid) || availableRosterOperators.length === 0}
                  onClick={() => setTeamRosterPickerOpen((previous) => !previous)}
                >
                  + member
                </TeamInlineAction>
                {teamRosterPickerOpen ? (
                  <AgentPickerMenu>
                    {availableRosterOperators.length ? (
                      availableRosterOperators.map((operator) => (
                        <AgentOption
                          key={`team-roster-${operator.id}`}
                          type="button"
                          onClick={() => {
                            void handleAddRosterMember(operator.id);
                          }}
                        >
                          <span>{operator.name}</span>
                          <AgentOptionSub>
                            {operator.email} · {operator.role}
                          </AgentOptionSub>
                        </AgentOption>
                      ))
                    ) : (
                      <AgentPickerEmpty>No additional admins or agents are available.</AgentPickerEmpty>
                    )}
                  </AgentPickerMenu>
                ) : null}
              </AgentPickerShell>
            ) : null}
          </TeamSectionHeader>
          <TeamSettingMeta>
            Current load is based on remaining estimated ticket time already assigned to each person on this team.
          </TeamSettingMeta>
          {teamRosterError ? <AIPanelNotice $tone="error">{teamRosterError}</AIPanelNotice> : null}
          {teamTicketsLoading ? <Placeholder>Loading team capacity…</Placeholder> : null}
          {!teamTicketsLoading && teamTicketsError ? (
            <AIPanelNotice $tone="error">{teamTicketsError}</AIPanelNotice>
          ) : null}
          {!teamTicketsLoading && !teamTicketsError ? (
            <TeamMembersList>
              {teamMemberSnapshots.length === 0 ? (
                <Placeholder>No active team contributors are carrying queued work yet.</Placeholder>
              ) : teamMemberSnapshots.map((snapshot) => {
                const isLead = cleanText(draftAppointedAgentUid) === snapshot.operator.id;
                const tone =
                  snapshot.capacityRatio >= 1
                    ? 'high'
                    : snapshot.capacityRatio >= 0.75
                      ? 'watch'
                      : 'low';
                return (
                  <TeamMemberRow key={snapshot.operator.id}>
                    <PortalAvatar
                      size={40}
                      uid={snapshot.operator.id}
                      email={snapshot.operator.email}
                      name={snapshot.operator.name}
                      photoURL={snapshot.operator.avatarUrl}
                    />
                    <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <TeamMemberName>{snapshot.operator.name}</TeamMemberName>
                        <StatusChip $tone={isLead ? 'success' : 'default'}>
                          {isLead ? 'Lead' : snapshot.operator.role}
                        </StatusChip>
                        {snapshot.isPrimaryMembership ? <StatusChip>Primary</StatusChip> : null}
                        {snapshot.isOutsideRoster ? <StatusChip $tone="warning">Outside roster</StatusChip> : null}
                      </div>
                      <TeamMemberMeta>
                        {snapshot.operator.email} • {snapshot.assignedTicketCount} assigned ticket
                        {snapshot.assignedTicketCount === 1 ? '' : 's'} • {formatHoursLabel(snapshot.remainingCapacityHours)} remaining
                      </TeamMemberMeta>
                    </div>
                    <TeamLoadWrap>
                      <TeamLoadValue>
                        {formatHoursLabel(snapshot.loadHours)} / {formatHoursLabel(teamSettingsDraft.teamCapacityHours)}
                      </TeamLoadValue>
                      <TeamLoadBar>
                        <TeamLoadFill $tone={tone} $width={snapshot.loadHours > 0 ? Math.max(4, snapshot.capacityRatio * 100) : 0} />
                      </TeamLoadBar>
                      {canEdit && snapshot.isRosterMember ? (
                        <TeamMemberActions>
                          <TeamMemberAction
                            type="button"
                            disabled={teamRosterSavingUid === snapshot.operator.id}
                            onClick={() => {
                              void handleRemoveRosterMember(snapshot.operator.id);
                            }}
                          >
                            {teamRosterSavingUid === snapshot.operator.id ? 'Removing…' : 'Remove'}
                          </TeamMemberAction>
                        </TeamMemberActions>
                      ) : null}
                    </TeamLoadWrap>
                  </TeamMemberRow>
                );
              })}
            </TeamMembersList>
          ) : null}
        </TeamFlatSection>
      </Panel>
    );
  };

  const renderPlaceholder = (label: string) => (
    <Placeholder>{label} is scaffolded for this node. Coming next step.</Placeholder>
  );

  const renderBody = () => {
    if (!node) {
      return <Placeholder>Select a node to inspect and edit its configuration.</Placeholder>;
    }
    if (tab === 'overview') return renderOverview();
    if (tab === 'routing') return renderRouting();
    if (tab === 'ai_agent') return renderAIAgent();
    if (tab === 'people') return renderTeam();
    return renderPlaceholder('Overview');
  };

  const drawerSubText = (() => {
    if (!node) return 'No selection';
    if (tab !== 'ai_agent') {
      return `${canEdit ? 'Editable' : 'Read only'} - Status: ${node.status}`;
    }
    const parts: string[] = [];
    if (selectedRoutingService) {
      parts.push(
        `${selectedRoutingService.serviceTitle}${selectedRoutingTier ? ` / ${selectedRoutingTier.name}` : ''}`,
      );
    }
    parts.push(node.title);
    parts.push(projectManagerDraft.active ? 'Node assignment active' : 'Node assignment inactive');
    return parts.join(' • ');
  })();

  return (
    <>
      <DrawerBackdrop
        type="button"
        $open={open}
        aria-label="Close details panel"
        onClick={onClose}
      />
      <Drawer
        $open={open}
        $childOpen={projectManagerGlobalScopeDrawerOpen}
      >
        <DrawerHeader>
          <DrawerTitleWrap>
            <DrawerTitle>{node?.title ?? 'Node Details'}</DrawerTitle>
            <DrawerSub>{drawerSubText}</DrawerSub>
          </DrawerTitleWrap>
          <CloseButton type="button" onClick={onClose} aria-label="Close details panel">
            ×
          </CloseButton>
        </DrawerHeader>
        <TabsWrap>
          <DrawerTabs activeTab={tab} onChange={setTab} />
        </TabsWrap>
        <DrawerBody>{renderBody()}</DrawerBody>
      </Drawer>
      <ChildDrawerBackdrop
        type="button"
        $open={projectManagerGlobalScopeDrawerOpen}
        aria-label="Close PM scopes"
        onClick={() => setProjectManagerGlobalScopeDrawerOpen(false)}
      />
      <ChildDrawer $open={projectManagerGlobalScopeDrawerOpen}>
        <ChildDrawerHeader>
          <ChildDrawerTitleWrap>
            <ChildDrawerTitle>Global PM scopes</ChildDrawerTitle>
            <ChildDrawerSub>
              Global node-level permissions for the planning agent. Organizations inherit
              these scopes unless their account overrides them.
            </ChildDrawerSub>
          </ChildDrawerTitleWrap>
          <ChildDrawerClose
            type="button"
            aria-label="Close PM scopes"
            onClick={() => setProjectManagerGlobalScopeDrawerOpen(false)}
          >
            ×
          </ChildDrawerClose>
        </ChildDrawerHeader>
        <ChildDrawerQuickActions>
          <AIAgentInlineButton
            type="button"
            disabled={!canEdit}
            onClick={() => {
              setProjectManagerGlobalScopes(DEFAULT_PROJECT_MANAGER_CLIENT_SCOPES);
            }}
          >
            Select all
          </AIAgentInlineButton>
          <AIAgentInlineButton
            type="button"
            $tone="danger"
            disabled={!canEdit}
            onClick={() => {
              setProjectManagerGlobalScopes([]);
            }}
          >
            Clear all
          </AIAgentInlineButton>
        </ChildDrawerQuickActions>
        <ScopeList>
          {PROJECT_MANAGER_CLIENT_SCOPE_SECTIONS.map((section) => (
            <ScopeSection key={section.key}>
              <ScopeSectionHeader>
                <ScopeSectionTitle>{section.title}</ScopeSectionTitle>
                <ScopeSectionHint>{section.hint}</ScopeSectionHint>
              </ScopeSectionHeader>
              <ScopeRows>
                {section.scopes.map((scope) => {
                  const active = projectManagerGlobalScopeValues.includes(scope.key);
                  return (
                    <ScopeRowButton
                      key={scope.key}
                      type="button"
                      $active={active}
                      disabled={!canEdit}
                      onClick={() => {
                        const nextScopes = active
                          ? projectManagerGlobalScopeValues.filter((entry) => entry !== scope.key)
                          : [...projectManagerGlobalScopeValues, scope.key];
                        setProjectManagerGlobalScopes(nextScopes);
                      }}
                    >
                      <ScopeCheck $active={active} />
                      <ScopeRowCopy>
                        <ScopeRowTitle>{scope.label}</ScopeRowTitle>
                        <ScopeRowHint>{scope.hint}</ScopeRowHint>
                      </ScopeRowCopy>
                    </ScopeRowButton>
                  );
                })}
              </ScopeRows>
            </ScopeSection>
          ))}
        </ScopeList>
      <ChildDrawerFooter>
          <RuleHint>
            These are the default PM permissions for this node. Organization-level overrides can
            still force human review or narrow the scope on a specific account.
          </RuleHint>
        </ChildDrawerFooter>
      </ChildDrawer>
      {projectManagerDeleteTarget ? (
        <ConfirmOverlay
          role="presentation"
          onClick={() => {
            if (projectManagerDeleting) return;
            setProjectManagerDeleteTarget(null);
          }}
        >
          <ConfirmDialog onClick={(event) => event.stopPropagation()}>
            <ConfirmTitle>Delete AI agent from this node?</ConfirmTitle>
            <ConfirmBody>
              This will remove <strong>{projectManagerDeleteTarget.agentTitle}</strong> from this
              brain node and clear its node-level planning assignment. This does not delete the AI
              agent definition itself.
            </ConfirmBody>
            <ConfirmActions>
              <AIAgentInlineButton
                type="button"
                disabled={projectManagerDeleting}
                onClick={() => setProjectManagerDeleteTarget(null)}
              >
                Cancel
              </AIAgentInlineButton>
              <AIAgentInlineButton
                type="button"
                $tone="danger"
                disabled={projectManagerDeleting}
                onClick={() => void confirmDeleteProjectManagerAssignment()}
              >
                {projectManagerDeleting ? 'Deleting...' : 'Delete agent'}
              </AIAgentInlineButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      ) : null}
    </>
  );
}
